/**
 * Layout behavior — 3D flexbox auto-layout
 *
 * Bridges the pure layout engine (src/layout/) with the scene graph.
 * All values in LayoutData and the engine are in meters.
 *
 * KEY DESIGN RULE: sizes come from LayoutData or from measured CONTENT
 * (a LayoutSizeProvider) — never from solver-written state.
 * - Container size: width/height/depth in LayoutData; Hug axes are baked
 *   back from the solve (engine-measured output, never a flex input).
 * - Child size with Layout/LayoutItem: its LayoutData — or, on Hug axes,
 *   a provider's measurement (Text line box, Model geometry × node scale),
 *   or, for a node with no Layout of its own that holds content, the box of
 *   what it holds (`_measureHeld`).
 * - Child size without either: not laid out (position only, no resize).
 * - Fill children: sized by the engine's flex distribution.
 *
 * The historical "never from transforms" phrasing guarded against one
 * feedback loop: reading back a size THE SOLVER wrote as if it were
 * authored intent. Content measurement is allowed because that loop stays
 * closed by construction:
 * - a provider DECLINES while a LayoutSizeReceiver shares its node — the
 *   receiver realizes the solver's box in geometry, so measuring it would
 *   echo the previous pass's output;
 * - Model measures its model subtree only (never solver-scaled child
 *   nodes), in the parent-local frame (never ancestor transforms);
 * - the solver leaf-scales only plain leaves (no provider/receiver), so a
 *   measured node's scale is user intent, never a solver write.
 *
 * Layout writes positions to all flowed children. It writes scale only to
 * leaf children with managed sizing (Layout or LayoutItem): plain leaves
 * get the slot size, providers/receivers/containers are held at identity —
 * except opted-in geometry groups and the leaf of a scale-preserving provider
 * (Model), whose node scale is the user's sizing handle and part of measurement.
 */

import type { AbstractMesh } from "@babylonjs/core/Meshes/abstractMesh";
import { Matrix, Quaternion, Vector3 } from "@babylonjs/core/Maths/math.vector";

import { TraceBehavior } from "./tracebehavior";
import type { LayoutData } from "../scenedata/behaviors/layoutdata";
import type { SceneObject } from "../core/SceneObject";
import { Properties } from "./properties";
import type { TraceScene } from "../core/TraceScene";
import { LayoutEngine } from "../layout/LayoutEngine";
import { isBakedGeometrySrc } from "../loaders/restoreBakedSrc";
import { getSavedPrimitiveType } from "../export/BuiltInPrimitives";
import type { IntrinsicSizeProvider } from "../layout/LayoutEngine";
import { LayoutTree } from "../layout/LayoutTree";
import { getContainerCrossAxes } from "../layout/LayoutRules";
import type { LayoutContainer, LayoutNode, Vector3D, LayoutItemInput, SizeMode } from "../layout/types";
import { createLayoutNode, createLayoutContainer, createLayoutItem, createPadding, LAYOUT_EPSILON } from "../layout/types";
import { LayoutItem } from "./layoutitem";
import { GeometrySizeProvider } from "./layoutGeometry";
import { Logger } from "../utils/Logger";
import { ASSET_PATIENCE_MS } from "../utils/AssetPatience";
import type { LayoutItemData } from "../scenedata/behaviors/layoutitemdata";


// ── Optional interface for behaviors that provide intrinsic sizes ──

/**
 * How a replaced element — content with its own intrinsic proportions —
 * meets a layout box authored independently of it. CSS `object-fit`'s
 * vocabulary, which is the right one: the question is identical, and the
 * engine already implemented three of these values without naming any.
 *
 * `none` is the value that carries the extra meaning here: it says the
 * node's transform scale belongs to the author, so layout must not write
 * it. Every other value hands layout the scale.
 */
export type LayoutFit = 'fill' | 'contain' | 'scale-down' | 'cover' | 'none';

/**
 * The frame a measured box is in, which is what a node that hugs what it holds
 * has to know to read it once (LAYOUT_ENGINE §5.3):
 *
 * - `node`: the node's own frame, as if it were neither turned nor scaled. A
 *   layout box (a Layout's, a LayoutItem's), Text's line box, MorphStretch's
 *   authored box. It is turned with the node and drawn at the node's scale, and
 *   stands where the node does.
 * - `parent`: where the node stands. An AABB in the node's parent's frame, its
 *   turn and scale already in it (Model measures its geometry so). Used as it is.
 */
export type LayoutContentFrame = 'node' | 'parent';

/**
 * Behaviors can implement this to provide measured content size for Hug sizing.
 *
 * A provider owns its visual size (e.g. Text sizes glyphs via fontSize), so
 * layout positions it but never writes scale — scaling measured content would
 * distort it. The optional centre hook reports where the rendered content's
 * visual centre sits relative to the node origin, letting layout centre
 * anchored content (anchorX "left" draws glyphs rightward of the origin) in
 * its slot instead of placing the anchor point there.
 */
export interface LayoutSizeProvider {
    /**
     * `false` when this behavior's content is sized BY the node scale rather
     * than measured for it, so layout must treat the node as a plain leaf and
     * write the slot box into its scale. Absent means `true`. Model sets it
     * for an export Bevel bake: a unit-size primitive standing in for the
     * `builtin:` cube or plane it replaced. It also sets it for a saved
     * built-in primitive (`./assets/primitive_<type>.glb`): the same unit
     * mesh, sized by the scale a Fixed slot wrote into it.
     */
    readonly providesLayoutSize?: boolean;

    /**
     * The frame `getLayoutContentSize()` and `getLayoutContentCenter()` answer
     * in. Absent means `node`: the box as if the node were not turned, which is
     * what a provider that measures its own content (Text, MorphStretch) has.
     * Model says `parent`, because it measures its geometry where it stands.
     * A node that hugs what it holds turns and scales a `node` box, and takes a
     * `parent` one as it is: each transform applied to it once.
     */
    readonly layoutContentFrame?: LayoutContentFrame;
    getLayoutContentSize(): Vector3D | null;
    getLayoutContentCenter?(): { x: number; y: number; z?: number } | null;

    /**
     * Optional speculative measurement at a proposed width, without changing
     * the provider's current wrap state. Layout uses the last resolved width,
     * or the authored Fixed width before a resolved constraint is available.
     */
    getLayoutContentSizeForWidth?(width: number): Vector3D | null;

    /**
     * Optional: receive the layout-resolved width after a solve, so the
     * provider can reflow its content to that width (Text re-breaks its
     * lines) and republish its measurement. `null` means "no constraint":
     * the node left layout management, or its width axis is Hug with no
     * authored maxWidth (the provider's own measurement sized it — wrapping
     * to it would echo the solver's output back as input). A Hug maxWidth is
     * passed as `width`, a wrapping ceiling. `slotWidth` is the slot layout
     * actually imposed, which text anchoring aligns to; null means align the
     * natural measured box (a ceiling is not a slot). Omitted, the slot is
     * `width`. The width-only shape is deliberate:
     * a full LayoutSizeReceiver realizes the whole slot box and is held to
     * it, while a wrapping provider still owns its measured size — the
     * resolved width is an input to measurement, not a replacement for it.
     */
    setLayoutResolvedWidth?(width: number | null, slotWidth?: number | null): void;

    /**
     * How this provider's content meets a slot whose proportions differ
     * from its own — CSS `object-fit`, named for a 3D box.
     *
     * ABSENT is not a value: it means the provider is glyph-sized and the
     * node scale is stale bake residue, so layout restores identity (Text).
     * Only a provider whose node scale is a real sizing handle declares a
     * fit, and then the declared value decides what layout writes:
     *
     * - `none`   — layout writes nothing. The node's transform scale is
     *              part of the measured size (a model's gizmo scale
     *              multiplies its natural bounds) and stays the author's.
     * - `contain`— uniform scale, largest that fits inside the slot.
     * - `scale-down` — `contain`, but never above 1: it shrinks content that
     *              does not fit and leaves content that does at its size.
     * - `cover`  — uniform scale, smallest that fills it. Honest only for
     *              content whose own geometry is the clip; a provider that
     *              would overflow may clamp to `contain` and say so.
     * - `fill`   — per-axis scale to the slot, distorting.
     *
     * Under any fitting value layout owns the scale and claims the field,
     * which is why the policy has to be authored rather than inferred.
     */
    readonly layoutFit?: LayoutFit;

    /**
     * Where fitted content sits on the slot's vertical axis: `center`
     * (bounds centre on the slot centre, the default) or `bottom` (bounds
     * seated on the slot floor). Read only when `layoutFit` is a fitting
     * value — a provider that keeps its own scale keeps its own place.
     */
    readonly layoutFitAnchor?: 'center' | 'bottom';

    /**
     * The content's size at node scale 1 — what `getLayoutContentSize()`
     * would report if the node's transform were identity.
     *
     * Required to answer a fit: the ratio a fit needs is slot ÷ INTRINSIC,
     * and `getLayoutContentSize()` deliberately folds the node scale in for
     * a scale-preserving provider, so it cannot supply it. Recovering it by
     * dividing the reported size by the current scale would compound every
     * pass's rounding into the next pass's input; a provider that fits
     * measures the un-scaled basis directly instead.
     *
     * Providers that never fit (`layoutFit` absent or `none`) omit it.
     */
    getLayoutIntrinsicSize?(): Vector3D | null;
}

/**
 * An extent below this is not a size — it is the depth axis of a design
 * drawn on a 2D canvas, where "flat" is the convention rather than an
 * instruction. Fitting against it would scale the content to nothing.
 */
const FIT_DEGENERATE_EXTENT = 1e-6;

/**
 * The scale a fit writes: slot ÷ intrinsic, reduced per the policy.
 *
 * Which axes constrain is the whole of the design here, and there are
 * exactly three exclusions, all of them cases where the axis is not really a
 * slot:
 *
 *   - a **Hug** axis was sized BY this content, so letting it constrain the
 *     fit is circular — the content would chase its own measurement;
 *   - a **degenerate** extent is 2D-canvas convention on the depth axis. A
 *     0.6 x 0.9 x 0.001 m slot means "a 0.6 x 0.9 card", and a contain fit
 *     that honoured the depth would shrink a 8 mm-thick phone by 8000x;
 *   - a **Fixed** axis the flex pass SHRANK below its authored box. Shrink is
 *     the container answering "I cannot give you the extent you asked for",
 *     and a refusal is not a slot. `contain` is uniform, so honouring one
 *     refused axis shrinks the content on ALL THREE by that axis's shortfall
 *     — which is not letterboxing, it is collapse. Measured: the
 *     iphone-screen device GLB, a 1.1969 x 2.4671 x 0.2040 m item inside a
 *     0.032 m-deep `direction: 'Z'` card, had its depth shrunk to 0.0277 and
 *     contain-fitted to 2.06 where its width and height both asked for 15.13
 *     — the phone rendered at an eighth of the slot it was told to fill. The
 *     honest reading of an item deeper than its container is the one every
 *     other overflowing flex item gets: it overflows on that axis.
 *
 * `Fixed` is the only mode this third test can be written against. It is the
 * one mode where `layoutBox.size` IS the author's number and stays it — the
 * measure pass bakes back on Hug axes only, and a `Fill` axis routinely
 * carries a stale survivor number (see `_applyItemFields`) that a legitimate
 * smaller resolve would read as a shrink.
 *
 * Everything left is a real constraint, and `contain`/`cover` are uniform
 * across all three axes by definition — an aspect-preserving fit that only
 * preserved two of them would not be preserving an aspect.
 *
 * Returns null when nothing constrains: an unconstrained fit is a no-op,
 * NOT a reset to identity (see the scale rule in `_applyResults`).
 *
 * KNOWN GAP — a Hug axis resolved to the SLOT is still excluded here. Under
 * `alignCross: 'Stretch'` the engine stretches a Hug cross-axis without
 * rewriting `sizeMode` (the same trap `widthIsOwnMeasurement` documents
 * below), so a real constraint reads as absent and the model overflows: a
 * 0.8 cube, Hug width stretched to a 1 m slot with a Fixed 3 m height,
 * contain-fits to 3.75 and ends up 3 m wide in that 1 m slot.
 *
 * The fix is NOT to reuse `widthIsOwnMeasurement`'s "resolved still equals
 * `layoutBox.size`" test. That predicate is sound for a wrap width, which
 * nothing downstream re-measures, and unsound here for two reasons:
 *   - dropped on its own it also excludes every Fixed axis, since
 *     `LayoutEngine.resolve` clamps `layoutBox.size` into `resolvedSize` there too;
 *   - kept as a conjunction with `sizeMode === 'Hug'` it does not converge.
 *     The measure pass bakes `geometry x nodeScale` into `layoutBox.size`,
 *     and the scale in that product is the one this function just chose —
 *     so satisfying the constraint makes the axis test as "own measurement"
 *     next pass, which drops it again. Measured: the scale 2-cycles
 *     1.25 -> 3.75 -> 1.25 forever.
 *
 * Converging needs the solver to record whether an axis was resolved from
 * an EXTERNAL extent (stretch / fill / percent / clamp) rather than copied
 * from the box — a LayoutNode field, not something recoverable here.
 */
export function computeFitScale(
    fit: LayoutFit,
    intrinsic: Vector3D,
    slot: Vector3D,
    sizeMode: { x: SizeMode; y: SizeMode; z: SizeMode },
    /** The node's AUTHORED box — `layoutBox.size`, never the resolved one. */
    box: Vector3D,
): Vector3D | null {
    const axes = ['x', 'y', 'z'] as const;
    const ratio: { x?: number; y?: number; z?: number } = {};
    for (const axis of axes) {
        if (sizeMode[axis] === 'Hug') continue;
        if (sizeMode[axis] === 'Fixed' && slot[axis] < box[axis] - LAYOUT_EPSILON) continue;
        if (slot[axis] <= FIT_DEGENERATE_EXTENT) continue;
        if (intrinsic[axis] <= FIT_DEGENERATE_EXTENT) continue;
        ratio[axis] = slot[axis] / intrinsic[axis];
    }
    const constrained = axes.filter(axis => ratio[axis] !== undefined);
    if (constrained.length === 0) return null;

    if (fit === 'fill') {
        // Unconstrained axes keep their intrinsic proportion rather than
        // collapsing — `fill` distorts to the slot, and an axis that is not
        // a slot has nothing to distort towards.
        return { x: ratio.x ?? 1, y: ratio.y ?? 1, z: ratio.z ?? 1 };
    }
    const ratios = constrained.map(axis => ratio[axis] as number);
    let uniform = fit === 'cover' ? Math.max(...ratios) : Math.min(...ratios);
    if (fit === 'scale-down') uniform = Math.min(uniform, 1);
    return { x: uniform, y: uniform, z: uniform };
}

/**
 * A child's box as {@link Layout._measureHeld} reads it: how big it is, and where
 * it is centred relative to the child's origin, each with the frame it is in
 * ({@link LayoutContentFrame}). They can differ: a centre hook answers for
 * itself, and the provider that answers it is not always the one that sized the
 * box (MorphStretch sizes a model, and the model centres it).
 *
 * A box in the child's own frame is drawn at the child's `scale`: 1 where layout
 * holds the node at identity (a layout box, and every provider node it manages:
 * their size is not a scale), the node's own where nothing does.
 */
interface HeldBox {
    size: Vector3D;
    sizeFrame: LayoutContentFrame;
    center: Vector3;
    centerFrame: LayoutContentFrame;
    scale: Vector3;
}

/** A node's local rotation as a quaternion, whichever way it is stored. */
function localRotation(transform: SceneObject['transform']): Quaternion {
    return transform.rotationQuaternion ?? Quaternion.FromEulerVector(transform.rotation);
}

export function isLayoutSizeProvider(obj: unknown): obj is LayoutSizeProvider {
    return typeof obj === 'object' && obj !== null &&
        'getLayoutContentSize' in obj &&
        typeof (obj as LayoutSizeProvider).getLayoutContentSize === 'function' &&
        (obj as LayoutSizeProvider).providesLayoutSize !== false;
}

/**
 * All LayoutSizeProviders on a node, in precedence order: a provider that
 * does NOT preserve node scale outranks one that does. A scale-snapping
 * provider (MorphStretch) realizes its box in geometry and holds the node at
 * identity, so its report IS the node's size; a `fit: none` one (Model)
 * measures geometry × node scale — when both co-exist the snapping provider
 * owns the node's sizing and the preserving measurement is at best redundant.
 * A model that DOES declare a fit is asking layout to size it, so it sorts
 * with the ordinary providers rather than to the back.
 * Attach order only ever breaks ties WITHIN a precedence class (a stable
 * partition, deliberately not a comparator sort), so the winner between the
 * two classes is deterministic no matter which behavior attached first.
 * Opted-in subtree geometry takes precedence over these node-only providers;
 * containers and size receivers retain their existing sizing authority.
 */
function getProvidersInPrecedenceOrder(sceneObject: SceneObject): LayoutSizeProvider[] {
    const providers: LayoutSizeProvider[] = [];
    for (const behavior of sceneObject.getBehaviors()) {
        if (isLayoutSizeProvider(behavior)) providers.push(behavior);
    }
    const ordered = providers.length < 2 ? providers : [
        ...providers.filter(p => p.layoutFit !== 'none'),
        ...providers.filter(p => p.layoutFit === 'none'),
    ];
    const measureGeometry = sceneObject.getBehavior(LayoutItem)?._getResolvedData().measureGeometry === true;
    if (measureGeometry && !sceneObject.getBehavior(Layout)
        && !sceneObject.getBehaviors().some(isLayoutSizeReceiver)) {
        return [new GeometrySizeProvider(sceneObject), ...ordered];
    }
    return ordered;
}

/**
 * Behaviors can implement this to receive the layout-resolved size and realize
 * it themselves (e.g. MorphStretch drives authored stretch morphs) instead of
 * being leaf-scaled — scale is uniform across a mesh, so it smears corner and
 * trim geometry that morphs are authored to preserve. A receiver node keeps
 * identity scale, exactly like a provider. With no receivers in a scene every
 * node takes the setScale path unchanged.
 */
export interface LayoutSizeReceiver {
    setResolvedSize(size: Vector3D): void;
    /**
     * Notified when the node leaves layout management (the bridge stops
     * handing over boxes), so the receiver can drop the stale resolved box and
     * fall back to its own sizing.
     *
     * Optional in the TYPE, expected in practice.
     *
     * Both departure sweeps call it with `?.` — `_releaseAll` on
     * detach/demotion, and the end-of-pass sweep in `_applyResults` — so
     * omitting it fails silently: the receiver keeps driving the last box it
     * was handed, and an authored size edit appears to do nothing until
     * reload. That is the bug #638 fixed for MorphStretch.
     *
     * Treat it as part of the contract, not an extra.
     */
    clearResolvedSize?(): void;
}

/**
 * THE receiver duck-type — exported so co-resident providers (Model) apply
 * the exact predicate `_applyResults` feeds boxes with, never a divergent
 * re-implementation.
 */
export function isLayoutSizeReceiver(obj: unknown): obj is LayoutSizeReceiver {
    return typeof obj === 'object' && obj !== null &&
        'setResolvedSize' in obj &&
        typeof (obj as LayoutSizeReceiver).setResolvedSize === 'function';
}

/**
 * A behavior that asks layout to fit its container's one child — the
 * `ContentSlot` behavior. Duck-typed like the providers above, so layout
 * reads it without importing the behavior (which imports layout).
 */
export interface ContentFitSource {
    readonly layoutContentFit: NonNullable<LayoutContainer['contentFit']>;
}

function isContentFitSource(obj: unknown): obj is ContentFitSource {
    return typeof obj === 'object' && obj !== null && 'layoutContentFit' in obj;
}

/** Slot children already warned about, so the message is one line per child and not one per solve. */
const warnedUnmanagedSlotChildren = new WeakSet<SceneObject>();

// ── Default size for children without explicit dimensions ──

/** Default child size in meters when no width/height specified */
const DEFAULT_SIZE = 1;

/** Minimum scale to prevent zero-scale invisible geometry (1mm) */
const MIN_VISIBLE_SCALE = 0.001;

// ── Layout behavior ───────────────────────────────────────

const MAX_GEOMETRY_PASSES = 3;

interface LayoutWidthFeed {
    owner: SceneObject;
    width: number | null;
}

/** A flowed child's last completed solve, in the parent container's local frame. */
export interface SolvedLayoutPlacement {
    parent: SceneObject;
    parentSize: Vector3D;
    slotCenter: Vector3D;
    slotSize: Vector3D;
    /** Local node translation the solve applied, including content-centre compensation. */
    position: Vector3D;
}

/** Inspection only (`Layout.observeSolves`): keep the slot a solve gave a child, and the translation it applied. */
function noteSolved(into: Map<SceneObject, SolvedLayoutPlacement>, child: SceneObject, parent: SceneObject | undefined, parentSize: Vector3D, slotCenter: Vector3D, slotSize: Vector3D, applied: Vector3D): void {
    if (parent) into.set(child, { parent, parentSize: { ...parentSize }, slotCenter, slotSize: { ...slotSize }, position: { x: applied.x, y: applied.y, z: applied.z } });
}

export class Layout extends TraceBehavior<LayoutData> {
    static readonly behaviorType = "Layout" as const;

    private _engine = new LayoutEngine();
    private _isComputingLayout = false;
    private _batchScheduled = false;
    private _geometryPasses = 0;
    private _hasGeometry = false;
    private _providers = new Map<SceneObject, LayoutSizeProvider[]>();
    private _solvedPlacements: Map<SceneObject, SolvedLayoutPlacement> | null = null;
    private static _observedScenes = new WeakSet<TraceScene>();

    /**
     * Inspection only. While on for a scene, each completed solve keeps the slot it gave every
     * flowed or inset-placed child, for `getSolvedPlacement`. Off by default: a solve then stores nothing.
     */
    static observeSolves(scene: TraceScene, on: boolean): void {
        if (on) Layout._observedScenes.add(scene); else Layout._observedScenes.delete(scene);
    }

    /** The last completed, non-deferred placement of a flowed or inset-placed child, or null when solves are not observed. Never starts a solve. */
    getSolvedPlacement(child: SceneObject): SolvedLayoutPlacement | null {
        const root = this._findLayoutRoot().getBehavior(Layout);
        const found = root?._solvedPlacements?.get(child);
        if (!root || root._batchScheduled || root._isComputingLayout || !found || child.getParent() !== found.parent || child.transform.isDisposed()) return null;
        return { parent: found.parent, parentSize: { ...found.parentSize }, slotCenter: { ...found.slotCenter },
            slotSize: { ...found.slotSize }, position: { ...found.position } };
    }

    /**
     * Receivers this (root) Layout handed a box to on its last pass. A node
     * that leaves layout management — LayoutItem removed, reparented away —
     * is simply not visited next pass, so departures are detected by diffing
     * against this set and notified via clearResolvedSize; otherwise the
     * stale slot box would override authored sizing forever.
     */
    private _fedReceivers = new Set<LayoutSizeReceiver>();

    /**
     * Providers this (root) Layout fed a resolved wrap width on its last
     * pass (setLayoutResolvedWidth). Same departure doctrine as
     * _fedReceivers: a node that leaves layout management is simply not
     * visited next pass, so the diff against this set is what retracts the
     * constraint (null) — otherwise text would stay wrapped to a slot that
     * no longer exists.
     */
    // The owner supports departure handoff; the width is the constraint the
    // next measure uses, including a Fixed width narrowed by flex shrinking.
    private _fedWidthProviders = new Map<LayoutSizeProvider, LayoutWidthFeed>();

    /**
     * Children this layout has itself flowed (written a position to) as
     * auto-positioned, keyed by transform id. Only these are eligible for the
     * position retraction below — see the comment at its use.
     *
     * The VALUE is the child's translation as it stood before that write, and
     * it is the value the retraction hands back. Re-reading the authored data
     * at that point would not do: outside Play the flow write makes the
     * solved position the authored one (the rest position), and the
     * retraction would then restore the very number it exists to undo.
     */
    private _flowedAsAuto = new Map<number, [number, number, number]>();

    /**
     * Children whose position/scale THIS layout currently owns, mirrored onto
     * each SceneObject via setRuntimeTransformFieldOwner (#662). The marks
     * record exactly which fields layout is writing, and each one it writes
     * goes to the live transform and through to the rest pose (a held
     * Properties baseline and, outside Play, authored TRS; setRestPose), so
     * captureTransform saves the arrangement as ordinary TRS for the export
     * conformance contract (docs/layout/UNITY_SUPPORT.md §1) whether or not a
     * baseline is held.
     * Rebuilt per pass and swapped (the _fedReceivers pattern) so departed
     * children get their marks cleared, not leaked.
     */
    private _managedFields = new Map<SceneObject, ManagedMarks>();

    /**
     * Where the content a Layout-less node hugs is centred, in that node's own
     * frame at scale 1, by node id. Written by the measure pass
     * (`_getIntrinsicSize`), read by `_applyResults` to seat the node by its
     * content rather than by its origin. Rebuilt per pass.
     */
    private _heldCenters = new Map<string, Vector3D>();

    /** The size the last pass solved for this container, metres; null before any pass has run. */
    private _solvedSize: Vector3D | null = null;

    /**
     * The size the solver gave this container in its last pass, whatever its sizing mode: unlike `width`/`height`/`depth`, which hold
     * the authored basis on a Fill axis and are baked back on Hug axes only. For tools that judge the arrangement the engine made.
     */
    getSolvedSize(): Vector3D | null {
        return this._solvedSize && { ...this._solvedSize };
    }

    protected override onAttach(): void {
        Layout._installReparentHook(this.sceneObject.traceScene);
        this._markDirty();
    }

    protected override onDetach(): void {
        if (this._patienceTimer !== null) {
            clearTimeout(this._patienceTimer);
            this._patienceTimer = null;
        }
        // The root Layout going away is itself a departure for everything it fed.
        this._releaseAll();
        // Removing a NESTED container's Layout takes its children out of
        // flow, but only the pass-running root holds their claims and fed
        // boxes — mark it dirty so the departure sweeps promptly instead of
        // waiting for some unrelated edit.
        const rootLayout = this._findLayoutRoot().getBehavior(Layout);
        if (rootLayout && rootLayout !== this) {
            rootLayout._scheduleBatch();
            return;
        }
        // When this behavior was the root, _releaseAll above retracted every
        // box it fed, down to the deepest receiver. The nearest Layouts below
        // it are roots of their own trees now and have never run a pass as
        // one, so nothing would feed those receivers again until an unrelated
        // edit. The pass is requested from a microtask, once `removeBehavior`
        // has spliced this one (`detach()` itself runs before the splice), and
        // only of a tree still standing (a detach can be part of disposing the
        // node).
        const pending = [...this.sceneObject.getChildren()];
        while (pending.length > 0) {
            const node = pending.pop()!;
            const nested = node.getBehavior(Layout);
            if (!nested) {
                pending.push(...node.getChildren());
                continue;
            }
            queueMicrotask(() => {
                if (!node.transform.isDisposed() && node.getBehavior(Layout) === nested) nested._scheduleBatch();
            });
        }
    }

    /**
     * Retract everything this layout holds as a pass-runner: fed receivers
     * (told to drop their stale slot box), the auto-flow repair set, and
     * every transform-field ownership claim. Called on detach, on the
     * emptied-tree early return, and on demotion detection — the paths where
     * this instance will not run the end-of-pass sweeps again.
     */
    private _releaseAll(): void {
        this._solvedPlacements = null;
        for (const receiver of this._fedReceivers) receiver.clearResolvedSize?.();
        this._fedReceivers.clear();
        // Same handoff as the end-of-pass sweep: this container is going
        // away, so each node falls back to its own entitlement rather than to
        // nothing. `removeBehavior` runs `detach()` before splicing, so this
        // Layout is still findable from the node — hence forced.
        for (const { owner } of this._fedWidthProviders.values()) {
            if (owner.transform.isDisposed()) continue;
            // Same "is it still ours to hand back" check as the end-of-pass
            // sweep: a node that already moved to another tree is that tree's
            // to size, even while this one is tearing down.
            if (!this._ownsDeparture(owner)) continue;
            LayoutItem.feedStandaloneWidth(owner, true);
        }
        this._fedWidthProviders.clear();
        this._flowedAsAuto.clear();
        for (const child of this._managedFields.keys()) {
            child.setRuntimeTransformFieldOwner(this, { position: false, scale: false });
        }
        this._managedFields.clear();
    }

    protected override async applyCommit(_patch: Partial<LayoutData>, previous: LayoutData): Promise<void> {
        // Authored commits pass a clone; property re-resolves pass the authored object itself.
        if (previous !== this.getAuthored()) this._restartGeometryBudget();
        this._markDirty();
    }

    /** Signal that a child's layout data changed and layout needs to recompute. */
    invalidate(options: { authored?: boolean } = {}): void {
        if (options.authored) this._restartGeometryBudget();
        this._markDirty();
    }

    private _restartGeometryBudget(): void {
        const root = this._findLayoutRoot().getBehavior(Layout);
        if (root && !root._isComputingLayout) root._geometryPasses = 0;
    }

    /**
     * Whether a layout holds this node's scale as a FIT: a number it derives
     * from content on every solve (a content slot's child, a fitted Model), as
     * against a scale it holds at identity, leaf-scales to a box, or leaves
     * alone. As of that layout's last solve.
     *
     * For an editor that takes such a node out of layout and has to give the fit
     * back: outside layout nothing derives it again, and the node would keep the
     * size of a slot it no longer stands in.
     */
    static isFitted(sceneObject: SceneObject): boolean {
        for (let ancestor = sceneObject.getParent(); ancestor; ancestor = ancestor.getParent()) {
            if (ancestor.getBehavior(Layout)?._managedFields.get(sceneObject)?.fitted) return true;
        }
        return false;
    }

    private _markDirty(): void {
        const root = this._findLayoutRoot();
        const rootLayout = root.getBehavior(Layout);
        if (rootLayout) {
            rootLayout._scheduleBatch();
        }
    }

    private _geometryTopology = '';

    private _geometryTopologyKey(): string {
        return JSON.stringify([this.sceneObject, ...this.sceneObject.getDescendants()].map(object => [
            object.getPath(), object.getBehaviors().map(behavior => behavior.name),
        ]));
    }

    private _scheduleBatch(): void {
        if (this._hasGeometry && this._geometryPasses >= MAX_GEOMETRY_PASSES) {
            // Adding/removing/reparenting nodes or behaviors starts a new solve;
            // observer feedback on the same authored tree keeps its spent budget.
            if (this._geometryTopologyKey() === this._geometryTopology) return;
            this._geometryPasses = 0;
        }
        if (this._batchScheduled) return;
        this._batchScheduled = true;
        queueMicrotask(() => {
            this._batchScheduled = false;
            // A microtask has no caller: a throw out of the solve lands as an
            // unhandled error in the host, outside every try/catch that asked
            // for the commit, and takes the session with it. Authored layout
            // data is the input here and nothing upstream guarantees it is
            // in-vocabulary, so the pass degrades instead — this container's
            // solve is skipped, loudly and in one line, and the rest of the
            // scene's roots keep solving on their own batches.
            try {
                this._runLayout();
            } catch (e) {
                Logger.error(
                    `[Layout] Layout pass failed on "${this.sceneObject.name}" ` +
                    `(node ${this.sceneObject.transform.uniqueId}) — skipping this pass: ` +
                    (e instanceof Error ? e.message : String(e))
                );
            }
        });
    }

    private _runLayout(): void {
        if (this._isComputingLayout) return;

        let baked: SceneObject[] = [];
        // A failed or deferred solve must not leave a previous placement looking current.
        this._solvedPlacements = null;
        this._hasGeometry = false;
        this._isComputingLayout = true;
        try {
            const tree = new LayoutTree();
            const nodeMap = new Map<string, SceneObject>();
            const hasManagedSizing = new Set<string>();
            const waiting = new Set<string>();
            this._nextDeadline = Infinity;
            this._heldCenters.clear();
            this._providers.clear();

            this._buildTree(this.sceneObject, tree, null, nodeMap, hasManagedSizing, waiting);

            const allNodes = tree.getAllNodes();
            if (allNodes.length <= 1) {
                // An emptied tree (childless container) never reaches the
                // departure sweeps at the end of _applyResults — everything
                // previously fed, flowed, or owned HAS departed, so release
                // it all here. Without this, a receiver whose removal left
                // the container empty keeps its stale slot box forever
                // (#657's failure mode, one level up), and stale ownership
                // claims would outlive the arrangement they described (#662).
                this._releaseAll();
                this._solvedSize = null;
                return;
            }

            const provider: IntrinsicSizeProvider = {
                getIntrinsicSize: (node: LayoutNode) => this._getIntrinsicSize(node, nodeMap, tree, hasManagedSizing),
                getMinSize: () => ({ x: 0, y: 0, z: 0 }),
            };
            this._engine.setIntrinsicProvider(provider);
            const geometryChanges = this._watchGeometry(tree, nodeMap);
            this._hasGeometry = geometryChanges.length > 0;
            if (this._hasGeometry) this._geometryTopology = this._geometryTopologyKey();
            if (this._hasGeometry) this._geometryPasses++;
            this._engine.computeLayout(tree);

            const deferred = this._collectDeferred(tree, waiting);
            this._applyResults(tree, nodeMap, hasManagedSizing, deferred);
            if (geometryChanges.some(changed => changed())) {
                if (this._geometryPasses < MAX_GEOMETRY_PASSES) this._scheduleBatch();
                else Logger.warn(`[Layout] Geometry on "${this.sceneObject.name}" did not settle after ${MAX_GEOMETRY_PASSES} passes.`);
            } else this._geometryPasses = 0;
            baked = this._bakeContainerSizes(tree, nodeMap, deferred);
        } catch (error) {
            this._hasGeometry = false;
            throw error;
        } finally {
            this._isComputingLayout = false;
            this._providers.clear();
            this._armPatience();
        }

        // Data signal before the lifecycle one, and both outside the guard:
        // whoever hears "a container's authored size changed" has the write
        // in hand by the time the solve reports complete.
        for (const container of baked) {
            this.sceneObject.traceScene._emitLayoutSizeBaked?.(container);
        }

        // Completion signal — fired outside the computing guard so a
        // listener that re-invalidates schedules the next microtask batch
        // instead of touching a live pass. Only a fully completed solve
        // reaches this line: the trivial-tree early return above (nothing
        // solved, nothing applied) and a throwing pass both skip it.
        this.sceneObject.traceScene._emitLayoutSolved?.(this.sceneObject);
    }

    /**
     * Bake each container's solved size into its authored width/height/depth
     * on HUG axes only. Containers are the one consumer with no other bake
     * channel: leaves persist through node scale, but a parent is actively
     * held at identity, so without this the authored size goes stale as
     * content changes and exports carry wrong container boxes.
     *
     * Fixed axes are user intent and are never written — in particular,
     * min/max clamping must not overwrite an authored Fixed value.
     *
     * Fill axes are deliberately NOT baked: the solver reads authored size
     * back as `measuredMain`, and for a container with children
     * computeFlexMin turns that into the flex content floor — so a baked
     * Fill result would freeze children at their high-water mark and block
     * every subsequent shrink (the row overflows permanently). Fill sizes
     * are distribution outputs; feeding them back turns the solver's own
     * output into its input. Regression-pinned below.
     *
     * Write mechanics: an epsilon-guarded direct write to authored+resolved
     * data — no commit, so no validation pass, no undo entry, and no
     * re-scheduled solve. Hug values are engine-measured outputs the solver
     * recomputes from children each pass, so writing them back cannot feed
     * the flex distribution.
     *
     * The data DID change, though, and inspector panels refresh on it — so
     * the containers actually written are returned for the caller to announce
     * through TraceScene's `onLayoutSizeBaked` hook, once the pass is off the
     * stack. Announcing through the scene keeps the editor's CustomEvent
     * plumbing on the editor side of the fence: a runtime/AR viewer runs the
     * same bake and simply has nobody subscribed.
     */
    private _bakeContainerSizes(
        tree: LayoutTree,
        nodeMap: Map<string, SceneObject>,
        deferred: Set<string>,
    ): SceneObject[] {
        const written: SceneObject[] = [];
        tree.traverse((node) => {
            const sceneObject = nodeMap.get(node.id);
            const layout = sceneObject?.getBehavior(Layout);
            if (!sceneObject || !layout) return;
            layout._solvedSize = { ...node.resolvedSize };
            // A deferred container's Hug measurement is taken across children
            // that are not all built (`_collectDeferred`), so it is not the
            // size to persist — the pass after the last load bakes the one
            // that is.
            if (deferred.has(node.id)) return;
            const data = layout._resolved;
            const size = node.resolvedSize;
            let baked = false;
            if (data.widthMode === 'Hug') baked = Layout._bakeAxis(layout, 'width', size.x) || baked;
            if (data.heightMode === 'Hug') baked = Layout._bakeAxis(layout, 'height', size.y) || baked;
            if (data.depthMode === 'Hug') baked = Layout._bakeAxis(layout, 'depth', size.z) || baked;
            if (baked) written.push(sceneObject);
        });
        return written;
    }

    /** Returns true when the axis value actually changed (epsilon-guarded). */
    private static _bakeAxis(layout: Layout, key: 'width' | 'height' | 'depth', value: number): boolean {
        const authored = layout.getAuthored();
        // A property-bound value stays bound — never stomp a $ref string.
        if (typeof authored[key] === 'string') return false;
        const prev = layout._resolved[key];
        if (typeof prev === 'number' && Math.abs(prev - value) < LAYOUT_EPSILON) return false;
        authored[key] = value;
        layout._resolved[key] = value;
        return true;
    }

    private _getProviders(object: SceneObject): LayoutSizeProvider[] {
        let providers = this._providers.get(object);
        if (!providers) {
            providers = getProvidersInPrecedenceOrder(object);
            const geometry = providers[0];
            if (geometry instanceof GeometrySizeProvider && !geometry.getLayoutContentSize()) providers = providers.slice(1);
            this._providers.set(object, providers);
        }
        return providers;
    }

    private _watchGeometry(tree: LayoutTree, nodeMap: Map<string, SceneObject>): Array<() => boolean> {
        const changes: Array<() => boolean> = [];
        for (const node of tree.getAllNodes()) {
            const object = nodeMap.get(node.id)!;
            const contentRoot = node.flowScale !== undefined;
            if (object.getBehavior(LayoutItem)?._getResolvedData().measureGeometry !== true) continue;
            const provider = this._getProviders(object)[0];
            if (provider instanceof GeometrySizeProvider) {
                changes.push(provider.watchBounds({ intrinsic: contentRoot, epsilon: LAYOUT_EPSILON }));
            }
        }
        return changes;
    }

    private _getIntrinsicSize(
        node: LayoutNode,
        nodeMap: Map<string, SceneObject>,
        tree: LayoutTree,
        hasManagedSizing: ReadonlySet<string>,
    ): Vector3D {
        const sceneObject = nodeMap.get(node.id);
        if (!sceneObject) return node.layoutBox.size;

        const fixedWidth = node.layoutBox.sizeMode.x === 'Fixed' && node.layoutBox.size.x > 0
            ? node.layoutBox.size.x
            : null;

        // A slot's content is measured at scale 1: layout writes its scale,
        // so a measurement that folds the node scale in (Model) would chase
        // its own fit.
        if (node.flowScale !== undefined) {
            for (const provider of this._getProviders(sceneObject)) {
                const basis = provider.getLayoutIntrinsicSize?.();
                if (basis) return basis;
            }
        }

        // Precedence order, then first non-null: an outranked provider is
        // still the fallback when the winner abstains (null means "no
        // measurement yet", not zero) — same abstain contract as ever.
        for (const provider of this._getProviders(sceneObject)) {
            if ((provider as unknown) === this) continue;
            // The last solve can narrow a Fixed width. Before it exists,
            // the authored width still gives the right first-pass height.
            const resolvedWidth = this._fedWidthProviders.get(provider)?.width;
            const width = resolvedWidth != null && resolvedWidth > 0 ? resolvedWidth : fixedWidth;
            const measured = width != null && provider.getLayoutContentSizeForWidth
                ? provider.getLayoutContentSizeForWidth(width)
                : provider.getLayoutContentSize();
            if (measured) return measured;
        }

        // No provider answers. A node that asks to hug (it has a LayoutItem)
        // and holds content beneath it hugs what it holds. Without a Layout
        // there is no flow to measure, and this used to fall through to the box
        // the LayoutItem carries, which is 1 x 1 x 1 unless authored: a wrapper
        // inside another was fitted as a unit cube, a 6 m group as 1 m. Only
        // the Hug axes take the result (the engine writes back Hug axes and no
        // others), so a node that is Fixed everywhere, or has no LayoutItem,
        // measures exactly as before. The LayoutItem test is also what keeps a
        // plain node's subtree from being walked for an answer nobody reads.
        if (node.childrenIds.length > 0 && hasManagedSizing.has(node.id)) {
            const held = this._measureHeld(sceneObject, tree, hasManagedSizing);
            if (held) {
                this._heldCenters.set(node.id, held.center);
                return held.size;
            }
        }
        this._heldCenters.delete(node.id);

        return node.layoutBox.size;
    }

    /**
     * The box of the content beneath a node that has no Layout: the union of
     * the boxes of what it holds, in the node's own frame at scale 1, with the
     * point that box is centred on. Null when nothing beneath is measurable.
     *
     * Measurable content is a child a size provider answers for, or a child
     * with a Layout or a LayoutItem (its box is already in the tree: measure
     * runs bottom-up). Anything else is passed through, and a plain group of
     * measurable things contributes what they hold, where the group stands.
     * Absolute children are out of flow, as they are for a container's Hug, and
     * stay out.
     *
     * Where a child stands is what its own transform says: layout places the
     * children of a container, and this node is not one. Nothing asks for a
     * solve when such a child moves, so a box measured here catches up at the
     * next solve, not at the move.
     *
     * Each box is in a frame ({@link LayoutContentFrame}), and each transform is
     * applied to it once. What is in the child's parent's frame (a model's
     * bounds) is where the child stands, turned and scaled, and is used as it
     * is. What is in the child's own frame (a layout box, a text's line box, a
     * morph-stretched piece's box) is scaled, turned and stood where the child
     * is.
     */
    private _measureHeld(
        root: SceneObject,
        tree: LayoutTree,
        hasManagedSizing: ReadonlySet<string>,
    ): { size: Vector3D; center: Vector3D } | null {
        const min = new Vector3(Infinity, Infinity, Infinity);
        const max = new Vector3(-Infinity, -Infinity, -Infinity);
        const include = (corner: Vector3, toRoot: Matrix): void => {
            const inRoot = Vector3.TransformCoordinates(corner, toRoot);
            min.minimizeInPlace(inRoot);
            max.maximizeInPlace(inRoot);
        };

        const visit = (parent: SceneObject, toRoot: Matrix): void => {
            for (const child of parent.getChildren()) {
                const id = String(child.transform.uniqueId);
                const held = tree.getNode(id);
                if (!held || held.layoutItem?.positionMode === 'Absolute') continue;
                const box = this._boxOf(child, held, hasManagedSizing);
                const transform = child.transform;
                const rotation = localRotation(transform);
                if (!box) {
                    visit(child, Matrix.Compose(transform.scaling, rotation, transform.position).multiply(toRoot));
                    continue;
                }
                // The centre and the size are each read in the frame they were
                // given in: one in the node's own frame is scaled and turned by
                // the node, one in its parent's is already there.
                const inParent = (v: Vector3): Vector3 => v.multiply(box.scale).applyRotationQuaternion(rotation);
                const centre = transform.position.add(box.centerFrame === 'node' ? inParent(box.center) : box.center);
                for (const sx of [-0.5, 0.5]) {
                    for (const sy of [-0.5, 0.5]) {
                        for (const sz of [-0.5, 0.5]) {
                            const offset = new Vector3(sx * box.size.x, sy * box.size.y, sz * box.size.z);
                            include(centre.add(box.sizeFrame === 'node' ? inParent(offset) : offset), toRoot);
                        }
                    }
                }
            }
        };
        visit(root, Matrix.Identity());

        if (min.x > max.x) return null;
        return {
            size: { x: max.x - min.x, y: max.y - min.y, z: max.z - min.z },
            center: { x: (min.x + max.x) / 2, y: (min.y + max.y) / 2, z: (min.z + max.z) / 2 },
        };
    }

    /**
     * A child's box for {@link _measureHeld}, or null when the child has none of
     * its own to give. A provider that answers wins, managed or not: its bounds
     * are what is rendered, where it stands. Otherwise a child with a Layout or
     * a LayoutItem gives the box layout holds for it.
     */
    private _boxOf(
        child: SceneObject,
        held: LayoutNode,
        hasManagedSizing: ReadonlySet<string>,
    ): HeldBox | null {
        const managed = hasManagedSizing.has(held.id);
        const providers = this._getProviders(child);
        for (const provider of providers) {
            const size = provider.getLayoutContentSize();
            if (!size) continue;
            // Per hook, as the position write reads it: the first provider that
            // answers the centre, which need not be the one that sized the box,
            // and answers in its own frame.
            let center = Vector3.Zero();
            let centerFrame: LayoutContentFrame = 'node';
            for (const p of providers) {
                const reported = p.getLayoutContentCenter?.() ?? null;
                if (!reported) continue;
                center = new Vector3(reported.x, reported.y, reported.z ?? 0);
                centerFrame = p.layoutContentFrame ?? 'node';
                break;
            }
            return {
                size,
                sizeFrame: provider.layoutContentFrame ?? 'node',
                center,
                centerFrame,
                // Layout holds a node it manages at identity (the scale rule in
                // `_applyResults`), so its box is what is drawn; a node nothing
                // manages is drawn at the scale it has.
                scale: managed ? Vector3.One() : child.transform.scaling.clone(),
            };
        }
        if (!managed) return null;
        // Another node that hugs what it holds has its content's centre; a
        // container's box stands on its origin.
        const own = this._heldCenters.get(held.id);
        return {
            size: held.layoutBox.size,
            sizeFrame: 'node',
            center: own ? new Vector3(own.x, own.y, own.z) : Vector3.Zero(),
            centerFrame: 'node',
            scale: Vector3.One(),
        };
    }

    /**
     * Where the content a Layout-less node hugs is centred, in its parent's
     * frame: what seats the node by its content instead of its origin. Only on
     * the axes it hugs (on a Fixed one the box is the author's, and the origin
     * sits at its centre as it always did). Null for a node that hugs nothing.
     */
    private _heldCenterOf(node: LayoutNode, sceneObject: SceneObject, scale: number): { x: number; y: number; z: number } | null {
        const held = this._heldCenters.get(node.id);
        if (!held) return null;
        const mode = node.layoutBox.sizeMode;
        const offset = new Vector3(
            mode.x === 'Hug' ? held.x : 0,
            mode.y === 'Hug' ? held.y : 0,
            mode.z === 'Hug' ? held.z : 0,
        ).scale(scale).applyRotationQuaternion(localRotation(sceneObject.transform));
        return { x: offset.x, y: offset.y, z: offset.z };
    }

    /**
     * Whether a node that left this pass is still ours to hand back its own
     * width — asked of the TREE, not of the nearest Layout.
     *
     * Only the root ever runs a pass: `_markDirty` redirects to
     * `_findLayoutRoot()._scheduleBatch()`, so a nested container's own Layout
     * never populates `_fedWidthProviders` and never sweeps. `this` is
     * therefore the root, and "is some Layout above the node" is the wrong
     * question — for anything under an intermediate container it answers with
     * that container, which is not the one sweeping, and the handoff never
     * happens. A paragraph that lost its LayoutItem two levels down stayed
     * wrapped to a width nothing was asserting any more.
     *
     * What actually disqualifies a node is belonging to a DIFFERENT layout
     * root: that tree solves it, and writing here would fight the solve.
     */
    private _ownsDeparture(owner: SceneObject): boolean {
        const authority = LayoutItem.nearestLayoutAuthority(owner);
        if (authority === null) return true;
        return authority._findLayoutRoot() === this.sceneObject;
    }

    private _findLayoutRoot(): SceneObject {
        let root = this.sceneObject;
        let parent = root.getParent();
        while (parent) {
            if (parent.getBehavior(Layout)) {
                root = parent;
            }
            parent = parent.getParent();
        }
        return root;
    }

    /**
     * Build a LayoutTree from the SceneObject hierarchy.
     *
     * Tracks which nodes have managed sizing (Layout or LayoutItem behavior)
     * so _applyResults knows whether to write scale.
     *
     * …and which CONTAINERS are still WAITING for a child. An object is
     * parented before its behaviors are built (they need the transform), so
     * between those two moments it is a live child that answers null to every
     * `getBehavior()` — including the `LayoutItem` its data declares. Reading
     * that as "declares nothing" is what put a set piece in a flow slot it
     * had opted out of. The object's own `isLoadingBehaviors` is the
     * difference between "no layout intent" and "not built yet", and the unit
     * of readiness is the CONTAINER: while one of its children is unbuilt,
     * this pass's picture of that container is partial, so
     * `_collectDeferred` takes the container (and whoever's size depends on
     * it) out of the WRITE, not the child out of the flow. Every one of them
     * is arranged on the pass that follows the last load —
     * `applyComponents` invalidates the nearest Layout when it closes the
     * window.
     */
    private _buildTree(
        sceneObject: SceneObject,
        tree: LayoutTree,
        parentId: string | null,
        nodeMap: Map<string, SceneObject>,
        hasManagedSizing: Set<string>,
        waiting: Set<string>,
    ): void {
        const behavior = sceneObject.getBehavior(Layout);

        // Demotion detection: only the pass-running ROOT accumulates fed/
        // flowed/claim state, so a DIFFERENT Layout in this tree holding any
        // was the root once — its object got wrapped in (or reparented
        // under) this container, a plain reparent with no detach — and it
        // will never run a pass again to sweep what it holds. Release it
        // all now. Claims are owner-keyed, so retracting the demoted
        // owner's entries can't touch the marks this pass writes.
        if (behavior && behavior !== this &&
            (behavior._managedFields.size > 0 ||
                behavior._fedReceivers.size > 0 ||
                behavior._fedWidthProviders.size > 0 ||
                behavior._flowedAsAuto.size > 0)) {
            behavior._releaseAll();
        }

        // Root must have a Layout behavior
        if (!behavior && parentId === null) return;

        // Use Babylon's unique ID (not name — names are not unique in the scene)
        const nodeId = sceneObject.transform.uniqueId.toString();

        let node: LayoutNode;
        if (behavior) {
            // A container is only as ready as its children: one of them mid-
            // build means the box this pass would solve for the rest of them
            // is provisional. Recorded here, acted on in `_collectDeferred`.
            let waits = false;
            for (const child of sceneObject.getChildren()) {
                if (this._isWaitingOn(child)) waits = true;
            }
            if (waits) waiting.add(nodeId);
            hasManagedSizing.add(nodeId);
            // Resolved, not authored: sizing fields may be property-bound
            // ("{w}") — the tree must see live numbers, not ref strings.
            node = this._dataToLayoutNode(nodeId, behavior._getResolvedData());
            const fit = Layout._contentFitFor(sceneObject, node);
            if (fit) node.layoutContainer!.contentFit = fit;
        } else {
            const itemBehavior = sceneObject.getBehavior(LayoutItem);
            if (itemBehavior) {
                // Child with LayoutItem — uses item data for sizing/flex
                hasManagedSizing.add(nodeId);
                node = this._itemDataToLayoutNode(nodeId, itemBehavior._getResolvedData());
            } else {
                // Child without Layout or LayoutItem — use its current scale as Fixed size.
                // Layout will position it but NOT resize it (see _applyResults).
                node = createLayoutNode(nodeId);
                const s = sceneObject.transform.scaling;
                node.layoutBox.size = {
                    x: Math.abs(s.x),
                    y: Math.abs(s.y),
                    z: Math.abs(s.z),
                };
                node.layoutBox.sizeMode = { x: 'Fixed', y: 'Fixed', z: 'Fixed' };
                node.layoutItem = createLayoutItem();
                node.layoutItem.mainAxisMode = 'Fixed';
            }
        }

        // The engine derives the real value; marking it here tells the
        // measure below to read this node's scale-1 basis.
        const parentNode = parentId !== null ? tree.getNode(parentId) : undefined;
        if (parentNode?.layoutContainer?.contentFit && node.layoutItem?.positionMode !== 'Absolute') {
            node.flowScale = 1;
        }

        tree.addNode(node, parentId ?? undefined);
        nodeMap.set(nodeId, sceneObject);

        // Always recurse into children — even non-container nodes may have
        // descendants with Layout behaviors (e.g. grouping nodes without Layout).
        // The engine uses layoutContainer to decide flex flow, not tree membership.
        for (const child of sceneObject.getChildren()) {
            this._buildTree(child, tree, nodeId, nodeMap, hasManagedSizing, waiting);
        }
    }

    /**
     * The fit a `ContentSlot` asks for on this container, or undefined when
     * none applies. It applies to exactly one flow child, and only a managed
     * one (Layout or LayoutItem): an unmanaged child's box IS its current
     * scale, so fitting it would feed the fit back into its own input.
     *
     * That last case is the one an editor user reaches by dropping a plain
     * model into a slot, and the slot then looks broken rather than refusing,
     * so it says so once per child (in the log, not per solve).
     */
    private static _contentFitFor(
        sceneObject: SceneObject,
        node: LayoutNode,
    ): LayoutContainer['contentFit'] {
        if (node.layoutContainer?.wrap === true) return undefined;
        let source: ContentFitSource | null = null;
        for (const behavior of sceneObject.getBehaviors()) {
            if (isContentFitSource(behavior)) { source = behavior; break; }
        }
        if (!source) return undefined;
        const flow = sceneObject.getChildren().filter(child => {
            const data = child.getBehavior(LayoutItem)?._getResolvedData()
                ?? child.getBehavior(Layout)?._getResolvedData();
            return data?.positionMode !== 'Absolute';
        });
        if (flow.length !== 1) return undefined;
        const child = flow[0];
        if (!child.getBehavior(Layout) && !child.getBehavior(LayoutItem)) {
            // A child still building answers null to everything it declares
            // (`isLoadingBehaviors`): not yet unmanaged, so not yet worth saying.
            if (!child.isLoadingBehaviors && !warnedUnmanagedSlotChildren.has(child)) {
                warnedUnmanagedSlotChildren.add(child);
                Logger.warn(
                    `[Layout] The ContentSlot on "${sceneObject.name}" does not fit "${child.name}": it has neither a Layout `
                    + 'nor a LayoutItem, so its box is its current scale and there is nothing to fit. Give it a LayoutItem (Hug).',
                );
            }
            return undefined;
        }
        return source.layoutContentFit;
    }

    /** When this pass-running Layout first saw a child mid-build, by transform id. */
    private _waitingSince = new Map<number, number>();

    /** Children already carrying a departure watch, by transform id. */
    private _departureWatched = new Set<number>();

    /** Timer that wakes the pass when the patience window runs out. */
    private _patienceTimer: ReturnType<typeof setTimeout> | null = null;

    /** The earliest patience deadline seen while building THIS tree. */
    private _nextDeadline = Infinity;

    /**
     * Is this container still waiting for `child` to be built?
     *
     * Three answers, and the two that are not "yes" are the reason this is a
     * method rather than a predicate inline:
     *
     * 1. **Not loading** — the ordinary answer. Its bookkeeping is dropped so
     *    a later reload starts a fresh window.
     * 2. **Loading, inside the patience window** — yes. The child is watched
     *    for DEPARTURE (below) and the window's end is remembered, so this
     *    container re-solves at the deadline even if nothing else wakes it.
     * 3. **Loading, past {@link ASSET_PATIENCE_MS}** — no. A fetch that never
     *    settles must not hold a container for the session: past the window
     *    the container is arranged WITHOUT that child, exactly as it would be
     *    for a child that declares nothing, and it re-arranges when the child
     *    finally lands (`applyComponents` invalidates on the way out). The
     *    one-arrangement guarantee is a guarantee about a load that finishes.
     *
     * DEPARTURE. A child that leaves while loading — disposed, or reparented
     * away — is the case that would otherwise strand the container: nothing
     * about a half-built object invalidates anything, so the pass that would
     * notice it is gone is never scheduled and its siblings sit unplaced for
     * good. Babylon's own dispose signal is the prompt wake-up; the reparent
     * hook below covers the move, in both directions.
     */
    private _isWaitingOn(child: SceneObject): boolean {
        const id = child.transform.uniqueId;
        if (!child.isLoadingBehaviors) {
            this._waitingSince.delete(id);
            return false;
        }
        const since = this._waitingSince.get(id) ?? Date.now();
        this._waitingSince.set(id, since);
        const deadline = since + ASSET_PATIENCE_MS;
        if (Date.now() >= deadline) {
            return false;
        }
        this._nextDeadline = Math.min(this._nextDeadline, deadline);
        if (!this._departureWatched.has(id)) {
            this._departureWatched.add(id);
            child.transform.onDisposeObservable.addOnce(() => {
                this._departureWatched.delete(id);
                this._waitingSince.delete(id);
                if (!this.sceneObject.transform.isDisposed()) this.invalidate();
            });
        }
        return true;
    }

    /**
     * Wake this container when the patience window on its slowest child runs
     * out, so the arrangement does not depend on that child ever settling.
     * Re-armed per pass at the earliest deadline still outstanding.
     */
    private _armPatience(): void {
        if (this._patienceTimer !== null) {
            clearTimeout(this._patienceTimer);
            this._patienceTimer = null;
        }
        if (this._nextDeadline === Infinity) return;
        const delay = Math.max(0, this._nextDeadline - Date.now());
        this._patienceTimer = setTimeout(() => {
            this._patienceTimer = null;
            if (this.sceneObject.transform.isDisposed()) return;
            Logger.warn(
                `[Layout] "${this.sceneObject.name}" waited ${ASSET_PATIENCE_MS / 1000}s for a child to finish `
                + `building — arranging without it; it will join when it arrives.`,
            );
            this.invalidate();
        }, delay);
    }

    /**
     * A loading object that MOVES has to wake both containers: the one it
     * left (which would otherwise wait for a child that is no longer its
     * own) and the one it joined (which does not yet know to wait).
     *
     * Installed once per scene, the same shape as `LayoutItem`'s reparent
     * hook and for the same reason: nothing in `setParent` invalidates
     * either side, and a half-built object has no behavior of its own to do
     * it. Objects that are not mid-build are left to the existing paths.
     * The same install subscribes to the scene's behaviors-loaded signal,
     * which wakes a container once its loading child is built.
     */
    private static _reparentHooked = new WeakSet<object>();
    private static _installReparentHook(scene: TraceScene | undefined): void {
        // Test harnesses and partial embeddings hand a behavior less than a
        // full TraceScene; the hook is timing, not correctness, so decline.
        if (!scene || typeof scene.onObjectReparented !== 'function') return;
        if (Layout._reparentHooked.has(scene)) return;
        Layout._reparentHooked.add(scene);
        scene.onObjectReparented((obj, prevParent) => {
            if (!obj.isLoadingBehaviors) return;
            if (prevParent && !prevParent.transform.isDisposed()) invalidateNearestLayout(prevParent);
            if (!obj.transform.isDisposed()) invalidateNearestLayout(obj);
        });
        // The other end of the wait: a container held for a loading child
        // is arranged on the pass after the child's behaviors are built.
        scene.onBehaviorsLoaded?.((obj) => {
            if (!obj.transform.isDisposed()) invalidateNearestLayout(obj);
        });
    }

    /**
     * Which nodes this pass must not arrange — the containers whose picture
     * of themselves is partial, plus the ones whose own arrangement would
     * change when those resolve.
     *
     * THE RULE. A container is not arranged while any of its direct children
     * is still building its behaviors: layout writes nothing inside it, so
     * every child — the one loading and the ones already there — keeps the
     * transform it arrived with, and the whole container is arranged once,
     * on the pass after its last child finishes. Taking the loading child out
     * of the flow instead was tried and is the same bug one object over: its
     * siblings get arranged around a gap it will fill, and jump when it
     * arrives.
     *
     * Deferral then follows SIZE dependence, so that no container is arranged
     * against a number that is still going to move:
     *
     * - UP, to a parent that measures a waiting child (Hug, and the flex
     *   floors a Fill basis feeds) — its own solve is provisional too;
     * - DOWN, to a child sized BY the deferred arrangement (Fill share,
     *   stretched cross axis) — its box is not final until its parent's is.
     *
     * A box that is `Fixed` on all three axes ends the chain in both
     * directions: its size is the author's number either way, so the solve
     * either side of it is already final. An Absolute child ends the upward
     * chain as well — the parent's measure filters it out, so its size can
     * never move the parent's.
     *
     * Positions do NOT propagate: a transform is local, so where a container
     * ends up does not change where its own children sit inside it.
     */
    private _collectDeferred(tree: LayoutTree, waiting: Set<string>): Set<string> {
        // Nothing is loading — the common case. `waiting` is empty and no
        // caller mutates what it gets back, so it doubles as the empty set.
        if (waiting.size === 0) return waiting;
        const deferred = new Set<string>();
        const sizeIsFinal = (node: LayoutNode): boolean =>
            node.layoutBox.sizeMode.x === 'Fixed'
            && node.layoutBox.sizeMode.y === 'Fixed'
            && node.layoutBox.sizeMode.z === 'Fixed';

        for (const id of waiting) {
            let node = tree.getNode(id);
            while (node && !deferred.has(node.id)) {
                deferred.add(node.id);
                const parent = node.parentId ? tree.getNode(node.parentId) : undefined;
                if (!parent?.layoutContainer) break;
                if (node.layoutItem?.positionMode === 'Absolute') break;
                if (sizeIsFinal(node)) break;
                node = parent;
            }
        }

        // Downward, after the upward closure is complete: an ancestor that
        // just joined carries its own subtree with it.
        const descended = new Set<string>();
        const descend = (node: LayoutNode): void => {
            if (descended.has(node.id)) return;
            descended.add(node.id);
            for (const child of tree.getChildren(node.id)) {
                if (sizeIsFinal(child)) continue;
                deferred.add(child.id);
                descend(child);
            }
        };
        for (const id of [...deferred]) {
            const node = tree.getNode(id);
            if (node) descend(node);
        }
        return deferred;
    }

    /**
     * Convert LayoutData to a LayoutNode (container + item fields).
     */
    private _dataToLayoutNode(id: string, data: LayoutData): LayoutNode {
        const node = this._applyItemFields(createLayoutNode(id), data);

        // Always set container properties on Layout nodes — the engine handles
        // empty containers correctly. This ensures gap/padding/alignment are
        // respected even before children are added.
        node.layoutContainer = createLayoutContainer();
        node.layoutContainer.directionAxis = data.direction ?? 'Y';
        node.layoutContainer.gap = data.gap ?? 0.02;
        node.layoutContainer.alignMain = data.alignMain ?? 'Start';
        node.layoutContainer.alignCrossA = data.alignCross ?? 'Start';
        node.layoutContainer.alignCrossB = data.alignCrossB ?? 'Center';
        node.layoutContainer.alignContentCrossA = data.alignContent ?? 'Start';
        node.layoutContainer.alignContentCrossB = data.alignContentCrossB ?? 'Start';
        node.layoutContainer.crossGap = data.crossGap;

        if (typeof data.padding === 'number') {
            node.layoutContainer.padding = createPadding(data.padding);
        } else if (data.padding) {
            node.layoutContainer.padding = {
                left: data.padding.left ?? 0,
                right: data.padding.right ?? 0,
                top: data.padding.top ?? 0,
                bottom: data.padding.bottom ?? 0,
                front: data.padding.front ?? 0,
                back: data.padding.back ?? 0,
            };
        } else {
            node.layoutContainer.padding = createPadding(0.02);
        }

        if (data.wrap != null) {
            node.layoutContainer.wrap = data.wrap;
        }
        // Wrap target axis: only meaningful on a wrapped container and never
        // the main axis — anything else falls back to the implicit first
        // cross axis (the engine also ignores values that aren't the second
        // cross axis, so invalid input degrades to today's behavior).
        if (data.wrap && data.wrapAxis && data.wrapAxis !== (data.direction ?? 'Y')) {
            node.layoutContainer.wrapAxis = data.wrapAxis;
        }

        return node;
    }

    /**
     * Convert LayoutItemData to a LayoutNode (item fields only, no container).
     */
    private _itemDataToLayoutNode(id: string, data: LayoutItemData): LayoutNode {
        return this._applyItemFields(createLayoutNode(id), data);
    }

    /**
     * Shared: populate sizing/flex/position item fields on a LayoutNode.
     * Used by both LayoutData and LayoutItemData (same field names).
     */
    private _applyItemFields(node: LayoutNode, data: LayoutItemInput): LayoutNode {
        const wMode = data.widthMode ?? 'Fixed';
        const hMode = data.heightMode ?? 'Fixed';
        const dMode = data.depthMode ?? 'Fixed';

        // A Fill axis has no authored extent — the parent decides it — so a
        // number arriving on one is not a size and must not be read as one.
        // The zero was already here for the absent case; the mode has to win
        // over a present value too, because a value gets there without an
        // author ever typing one: anything that round-trips schema-resolved
        // data (defaults filled in) writes the default back as authored, and
        // flipping a mode from Fixed to Fill leaves the old number behind.
        //
        // A survivor is not inert. `computeFlexMin` implements CSS
        // `min-height:auto`, whose floor for a Fill item is that item's own
        // MEASURED content — and the measure pass writes `layoutBox.size` on
        // Hug axes only (bake-back skips Fixed and Fill alike), so on a Fill
        // axis the floor reads whatever came in from data. A Fill child in a
        // container with nothing left to give then froze at that number
        // instead of collapsing: a card 0.96 m tall held its body slot 1.5 m
        // open, and everything under the slot's first overflowing sibling
        // sat half a metre low.
        //
        // The authored number STAYS on a Fill axis. It is the CSS flex-basis
        // — `flex: 1 1 200px` — and two passes read it as one: line breaking
        // measures a candidate line against it, and a Hug ancestor measures
        // across it. Zeroing it here silently stopped every Fill child from
        // wrapping and made a Hug container measure 0 across one.
        //
        // What the number is NOT is measured content, which is what made it
        // wrong as a `min-height:auto` floor — that is fixed where the floor
        // is computed instead (LayoutEngine.hasMeasuredContentFloor).
        //
        // An ABSENT number on a Fill axis is still 0, as it always was. A
        // Fill child with no authored size is what a converter writes when it
        // means "take a share" and nothing more (trace-sandbox convert.ts's
        // itemFields does exactly this); defaulting it to DEFAULT_SIZE would
        // feed a metre of phantom basis into Hug measurement and into wrap
        // line-breaking, quietly widening ancestors and breaking lines that
        // used to fit. Only an authored number is a basis.
        const fillBasis = (mode: SizeMode, authored: number | undefined): number =>
            authored ?? (mode === 'Fill' ? 0 : DEFAULT_SIZE);
        node.layoutBox.size = {
            x: fillBasis(wMode, data.width),
            y: fillBasis(hMode, data.height),
            z: fillBasis(dMode, data.depth),
        };
        node.layoutBox.sizeMode = { x: wMode, y: hMode, z: dMode };
        // …and on a Fill axis that number is a basis, never a measurement.
        node.layoutBox.sizeIsBasisX = wMode === 'Fill';
        node.layoutBox.sizeIsBasisY = hMode === 'Fill';
        node.layoutBox.sizeIsBasisZ = dMode === 'Fill';

        // Authored min/max — floors and ceilings for the engine's clamp and
        // flex freeze loops. Absent axes keep the open defaults (0/Infinity).
        // A min above its max resolves min-wins (the CSS rule), matching the
        // engine's own clamp — a state reachable mid-edit whenever one field
        // commits before the other, and resolving it the other way would
        // leave resolvedSize below min and trip the assertMinSize invariant.
        const minW = data.minWidth ?? 0;
        const minH = data.minHeight ?? 0;
        const minD = data.minDepth ?? 0;
        node.layoutBox.min = { x: minW, y: minH, z: minD };
        node.layoutBox.max = {
            x: Math.max(minW, data.maxWidth ?? Infinity),
            y: Math.max(minH, data.maxHeight ?? Infinity),
            z: Math.max(minD, data.maxDepth ?? Infinity),
        };
        // Explicit mins opt Fill items out of the content-based floor
        // (CSS min-width:0 pattern) — authored zero counts as explicit.
        node.layoutBox.minExplicitX = data.minWidth != null;
        node.layoutBox.minExplicitY = data.minHeight != null;
        node.layoutBox.minExplicitZ = data.minDepth != null;

        node.layoutItem = createLayoutItem();
        node.layoutItem.mainAxisMode = 'Fixed'; // Resolved by engine pre-pass
        // flexGrow 0 means "auto": the engine grows Fill items with weight 1
        // and leaves Fixed/Hug items alone. Only explicit > 0 overrides that.
        const authoredGrow = data.flexGrow ?? 0;
        node.layoutItem.flexGrow = authoredGrow > 0 ? authoredGrow : undefined;
        node.layoutItem.flexShrink = data.flexShrink ?? 1;
        node.layoutItem.positionMode = data.positionMode ?? 'Auto';
        // Copied through UNDEFAULTED like the alignments below: absent is a
        // statement here — "nobody placed this, keep your own transform" —
        // and substituting {left: 0, top: 0} would silently drag every
        // existing Absolute child to its parent's top-left corner.
        node.layoutItem.inset = data.inset;
        // Copied through UNDEFAULTED, unlike everything above it: absent means
        // "inherit the container", which the engine resolves at solve time
        // (`item?.alignSelfCross ?? container.alignCrossA`). Substituting a
        // default here would freeze the container's value at commit time and
        // the child would stop following it.
        node.layoutItem.alignSelfCross = data.alignSelfCross;
        node.layoutItem.alignSelfCrossB = data.alignSelfCrossB;

        return node;
    }

    /**
     * Does `parent` hand `child` ITS OWN inner X extent, rather than letting
     * the child size itself on that axis?
     *
     * The predicate the wrap-width walk below turns on, and it is a mirror of
     * `LayoutEngine.resolveChildSizes` (`layout/LayoutEngine.ts:1056-1069`)
     * rather than an inference from the numbers those lines produced. The
     * engine imposes the container's inner cross extent when — and only when —
     * one of three things holds for the child's X:
     *
     *   - an authored `widthPercent` (a share of the container's inner box);
     *   - `sizeMode.x === 'Fill'`;
     *   - the EFFECTIVE cross alignment on the axis X happens to occupy is
     *     `Stretch`, and the child is not `Fixed` there.
     *
     * "Effective" is the whole of it. The alignment that decides is
     * `item.alignSelfCross ?? container.alignCrossA` when X is the container's
     * FIRST cross axis and `item.alignSelfCrossB ?? container.alignCrossB`
     * when it is the second — which of those two X occupies depends on
     * `directionAxis` and, for a wrapping container, on `wrapAxis`, which is
     * exactly what `getContainerCrossAxes` answers. A per-child `alignSelf*`
     * overrides its container's value, so reading the container's field alone
     * gets a Stretch-in-a-Start-container child (and the reverse) backwards.
     *
     * Two exclusions matter as much as the rule:
     *
     *   - X on the container's MAIN axis carries no cross alignment at all.
     *     There a Hug child's width IS its own flex basis, and nothing but
     *     grow/shrink moves it. A lone (or widest) flow child of a main-axis
     *     container has a resolved width equal to its parent's inner width by
     *     construction — which is why a size-equality test reads "the parent
     *     stretched me" here and is wrong every time.
     *   - an Absolute child is placed against its containing box, not flowed
     *     (`LayoutEngine.ts:668-678`): `Fill` and percent still take the
     *     containing extent, but nothing else does — alignment is not consulted.
     */
    private _xIsImposedByParent(parent: LayoutNode, child: LayoutNode): boolean {
        const container = parent.layoutContainer;
        if (!container) return false;
        // A slot's content is fitted, not sized: its width is its own.
        if (child.flowScale !== undefined) return false;
        const item = child.layoutItem;
        const percent = item?.widthPercent;
        if (typeof percent === 'number' && Number.isFinite(percent)) return true;
        if (child.layoutBox.sizeMode.x === 'Fill') return true;
        if (item?.positionMode === 'Absolute') return false;
        const [crossA, crossB] = getContainerCrossAxes(container);
        const align = crossA === 'X'
            ? (item?.alignSelfCross ?? container.alignCrossA)
            : crossB === 'X'
                ? (item?.alignSelfCrossB ?? container.alignCrossB)
                : undefined;
        return align === 'Stretch' && child.layoutBox.sizeMode.x !== 'Fixed';
    }

    /**
     * The wrap width for a Hug text whose resolved width is its OWN
     * measurement (`widthIsOwnMeasurement` below) — walking out past every
     * ancestor that is equally self-referential to the nearest one that is a
     * real constraint.
     *
     * The case: a Hug text sits in a Hug container that stretches its
     * children. The container hugs to the text's measurement and then hands
     * that same measurement straight back, so the text's resolved width IS
     * its own — `widthIsOwnMeasurement` true — and the feed below sends null,
     * i.e. "wrap at nothing". The text measures as one line and overruns,
     * even though something further out (the Fixed column the whole stack
     * lives in) was the real constraint the entire time.
     *
     * The rule: find the nearest ancestor with a width that did not come from
     * this content, then propagate it back down, shedding each intermediate
     * container's left+right padding. This runs in `_applyResults`, after the
     * whole tree is solved, so `resolvedSize` is final everywhere and "not
     * from this content" reduces to the same test the leaf already gets,
     * applied up the chain.
     *
     * Every hop is gated on `_xIsImposedByParent`: the chain is only a chain
     * while each link actually transmits. A Start/Center/End-aligned Hug child
     * was never bounded by its parent, and inventing a width for it
     * manufactures wrapping — and, downstream, width overruns — that the
     * authored layout does not ask for.
     *
     * Terminates and converges: the anchor is by construction a width this
     * text did not produce, so wrapping to it shrinks the text and every Hug
     * level in between without changing the number fed on the next pass.
     *
     * Returns null when the first hop does not transmit, or when the walk
     * reaches the root without finding a real constraint (an authored Hug
     * chain with nothing Fixed above it): the caller's existing
     * unconstrained-null behaviour stands.
     */
    private _findAncestorWrapWidth(tree: LayoutTree, node: LayoutNode, parent: LayoutNode): number | null {
        let child = node;
        let cur: LayoutNode | undefined = parent;
        let paddingToShed = 0;
        while (cur) {
            if (!this._xIsImposedByParent(cur, child)) return null;
            const ownPadding = cur.layoutContainer
                ? cur.layoutContainer.padding.left + cur.layoutContainer.padding.right
                : 0;
            const selfReferential =
                cur.layoutBox.sizeMode.x === 'Hug'
                && Math.abs(cur.resolvedSize.x - cur.layoutBox.size.x) <= LAYOUT_EPSILON;
            if (!selfReferential) {
                const width = cur.resolvedSize.x - ownPadding - paddingToShed;
                return width > LAYOUT_EPSILON ? width : null;
            }
            paddingToShed += ownPadding;
            child = cur;
            cur = cur.parentId ? tree.getNode(cur.parentId) : undefined;
        }
        return null;
    }

    /**
     * Re-assert, for a node this pass skipped, everything the last pass held
     * about it.
     *
     * The four maps rebuilt by `_applyResults` are swapped in wholesale at
     * the end of the pass, so "not visited" reads as "departed": a receiver
     * would be told to drop its resolved box, a provider handed back its
     * standalone width, an ownership claim retracted, and the record that
     * makes the Absolute retraction possible forgotten. A node inside a
     * deferred container has departed nothing — it is exactly where the last
     * arrangement put it — so its entries carry over untouched.
     */
    private _carryForward(
        sceneObject: SceneObject,
        fedNow: Set<LayoutSizeReceiver>,
        fedWidthNow: Map<LayoutSizeProvider, LayoutWidthFeed>,
        flowedNow: Map<number, [number, number, number]>,
        managedNow: Map<SceneObject, ManagedMarks>,
    ): void {
        // The FIRST receiver, exactly as `_applyResults` picks it — it is the
        // only one that can have been fed.
        for (const behavior of sceneObject.getBehaviors()) {
            if (!isLayoutSizeReceiver(behavior)) continue;
            if (this._fedReceivers.has(behavior)) fedNow.add(behavior);
            break;
        }
        for (const provider of this._getProviders(sceneObject)) {
            const feed = this._fedWidthProviders.get(provider);
            if (feed?.owner === sceneObject) {
                fedWidthNow.set(provider, feed);
            }
        }
        const transformId = sceneObject.transform.uniqueId;
        const flowed = this._flowedAsAuto.get(transformId);
        if (flowed) flowedNow.set(transformId, flowed);
        const marks = this._managedFields.get(sceneObject);
        if (marks) managedNow.set(sceneObject, marks);
    }

    /**
     * Write resolved layout back to scene object transforms.
     *
     * Position: written for ALL children (layout controls arrangement).
     * Scale: written ONLY for children with managed sizing (Layout or LayoutItem).
     *        Children without either keep their scale — layout just moves them.
     *
     * …except inside a DEFERRED node (`_collectDeferred`), where this pass
     * writes nothing at all.
     */
    private _applyResults(
        tree: LayoutTree,
        nodeMap: Map<string, SceneObject>,
        hasManagedSizing: Set<string>,
        deferred: Set<string>,
    ): void {
        const roots = tree.getRoots();
        if (roots.length === 0) return;

        const fedNow = new Set<LayoutSizeReceiver>();
        const fedWidthNow = new Map<LayoutSizeProvider, LayoutWidthFeed>();
        // Rebuilt each pass and swapped in at the end (the _fedReceivers
        // pattern): a node that leaves layout management is simply never
        // visited again, so delete-on-repair alone leaks its id for the
        // root Layout's lifetime (#658).
        const flowedNow = new Map<number, [number, number, number]>();
        const placementsNow = Layout._observedScenes.has(this.sceneObject.traceScene) ? new Map<SceneObject, SolvedLayoutPlacement>() : null;
        const managedNow = new Map<SceneObject, ManagedMarks>();
        tree.traverse((node) => {
            const sceneObject = nodeMap.get(node.id);
            if (!sceneObject) return;

            // Inside a deferred node: this pass has a partial picture of the
            // arrangement, so it writes none of it and the children stay as
            // they arrived. Everything this node held from the LAST pass is
            // carried over — a skip is not a departure, and treating it as
            // one would retract the resolved sizes and ownership marks of
            // siblings that have been settled for passes.
            //
            // Skipping the node, not the traversal: a container further down
            // whose own size is final (`_collectDeferred`) is not deferred,
            // and arranges its children on their own merits.
            if (node.parentId && deferred.has(node.parentId)) {
                this._carryForward(sceneObject, fedNow, fedWidthNow, flowedNow, managedNow);
                return;
            }

            // A size-provider behavior (e.g. Text) owns its visual size —
            // layout positions it, never scales it. The precedence WINNER
            // (index 0) governs the fit/scale rule — with
            // MorphStretch and Model co-resident the snapping winner must
            // win it, or Model's `fit: none` would veto the identity
            // restore the morph-driven node depends on. Per-provider HOOKS
            // (centre compensation below) instead resolve per hook with
            // abstain fall-through, so an outranked provider's answer is
            // still consulted when the winner has none.
            const managed = hasManagedSizing.has(node.id);
            const providers = this._getProviders(sceneObject);
            const provider: LayoutSizeProvider | null = providers[0] ?? null;
            let receiver: LayoutSizeReceiver | null = null;
            for (const behavior of sceneObject.getBehaviors()) {
                if (isLayoutSizeReceiver(behavior)) { receiver = behavior; break; }
            }

            if (!node.parentId) return;

            const parent = tree.getNode(node.parentId);
            if (!parent) return;

            // Only children of declared containers are FLOWED. A plain
            // grouping node is pass-through for tree membership (container
            // grandchildren still solve, managed nodes still get sized), but
            // its children keep their authored positions — writing engine
            // positions onto freeform content collapses hand-placed
            // arrangements into a pile at the group origin.
            const parentIsContainer = !!parent.layoutContainer;

            const pos = node.resolvedPosition;
            const size = node.resolvedSize;
            const parentSize = parent.resolvedSize;

            // Absolute children keep their AUTHORED transform position —
            // layout only resolves their size (Fill/percent vs parent). Where
            // layout HAS flowed such a child before (it was an ordinary auto
            // child until the editor made it Absolute), the position is
            // actively RESTORED rather than skipped, the same doctrine as the
            // identity-restore for scale below — otherwise the stale write
            // would stand for good.
            //
            // FIT — before anything reads the content's bounds.
            //
            // A provider that declares a fit has handed layout its scale, so
            // layout writes it here rather than in the scale rule below. The
            // ORDER is load-bearing: the centre hook a few lines down asks
            // the provider where its geometry's centre sits, and the fitted
            // scale moves it. Writing the scale afterwards would place the
            // node using the previous pass's centre and correct itself only
            // on the next solve — a visible one-frame jump on every reflow.
            //
            // Scoped to plain managed leaves, exactly like the scale rule:
            // a container's size is a logical frame (scaling it compounds
            // onto children) and a receiver realizes its box in geometry.
            const isLeaf = node.childrenIds.length === 0;
            const fit = provider?.layoutFit;
            let fitScaleWritten = false;
            // The scale this pass derived as a fit; it becomes the node's rest
            // scale below.
            let derivedFit: [number, number, number] | undefined;
            // A slot's content: the engine derived its scale. Written here,
            // before the centre hook reads bounds, like a provider fit. A
            // plain leaf is leaf-scaled to its box, so it takes the box times
            // the fit; anything else is held at the fit itself.
            const flowScale = node.flowScale;
            if (flowScale !== undefined) {
                const plainLeaf = isLeaf && !provider && !receiver;
                const target = plainLeaf
                    ? new Vector3(
                        (size.x || MIN_VISIBLE_SCALE) * flowScale,
                        (size.y || MIN_VISIBLE_SCALE) * flowScale,
                        (size.z || MIN_VISIBLE_SCALE) * flowScale,
                    )
                    : new Vector3(flowScale, flowScale, flowScale);
                derivedFit = [target.x, target.y, target.z];
                const current = sceneObject.transform.scaling;
                if (Math.abs(current.x - target.x) > LAYOUT_EPSILON
                    || Math.abs(current.y - target.y) > LAYOUT_EPSILON
                    || Math.abs(current.z - target.z) > LAYOUT_EPSILON) {
                    sceneObject.setScale(target, null);
                }
            } else if (managed && isLeaf && !receiver && fit && fit !== 'none') {
                const intrinsic = provider?.getLayoutIntrinsicSize?.() ?? null;
                const fitted = intrinsic
                    ? computeFitScale(
                        fit, intrinsic, node.resolvedSize,
                        node.layoutBox.sizeMode, node.layoutBox.size,
                    )
                    : null;
                if (fitted) {
                    derivedFit = [fitted.x, fitted.y, fitted.z];
                    const current = sceneObject.transform.scaling;
                    if (Math.abs(current.x - fitted.x) > LAYOUT_EPSILON
                        || Math.abs(current.y - fitted.y) > LAYOUT_EPSILON
                        || Math.abs(current.z - fitted.z) > LAYOUT_EPSILON) {
                        sceneObject.setScale(new Vector3(fitted.x, fitted.y, fitted.z), null);
                        // The write invalidates this pass's own measurements.
                        // contain/cover are UNIFORM: the ratio skips a Hug
                        // axis, but the scale still lands on it, so the
                        // geometry along that axis just changed size — and
                        // `_bakeContainerSizes` runs later in THIS pass off
                        // the pre-write tree, baking the stale measurement
                        // into every Hug ancestor's authored box (a 0.8-tall
                        // slot around content the fit just took to 2.0).
                        // Nothing else re-solves, so ask for one more pass.
                        //
                        // It terminates: `getLayoutIntrinsicSize` measures in
                        // the node's OWN frame and is unchanged by the scale
                        // written here, so the next pass recomputes the same
                        // ratio, hits the epsilon-equal path above, and
                        // neither writes nor reschedules again. Scheduling
                        // from inside a pass is safe — `_scheduleBatch` defers
                        // to a microtask and `_isComputingLayout` guards the
                        // re-entry, the same contract `invalidate()` relies on.
                        this._scheduleBatch();
                    }
                    fitScaleWritten = true;
                }
            }

            const isAbsolute = node.layoutItem?.positionMode === 'Absolute';
            // An Absolute child that authored an `inset` has HANDED layout its
            // position: a corner offset is only meaningful once both boxes are
            // solved, so the conversion belongs here and nowhere earlier. It
            // therefore takes the flow branch's formula rather than the
            // restore branch above — and, being a position layout wrote, it is
            // recorded in `flowedNow` so that dropping the inset later hands
            // the transform back through exactly that restore.
            //
            // Presence, not truthiness: `{}` is still an author saying "place
            // me at the corner".
            const placedByInset = isAbsolute && node.layoutItem?.inset != null;
            const transformId = sceneObject.transform.uniqueId;
            if (isAbsolute && !placedByInset && parentIsContainer && this._flowedAsAuto.has(transformId)) {
                // Undo a position THIS layout wrote, and only that.
                //
                // The departure doctrine, for positions: a child that leaves
                // flow gets back what it had before flow took it over — the
                // same rule `_fedReceivers` applies to slot boxes. It is
                // reached when a child layout was flowing is given
                // `positionMode: 'Absolute'` in the editor; without it, the
                // stale flow position would stand for good.
                //
                // It is not how a LOADING scene is handled any more: a
                // container waits for its children rather than flowing the
                // ones that happen to be built (`_collectDeferred`), and the
                // window that says "still building" opens when an object is
                // PARENTED on the paths that parent before they build
                // (`loadFromPluginResult`, `_placeReferencedDocument`) — so a
                // set piece is never flowed before its LayoutItem exists and
                // never needs putting back. The one way back into this branch
                // during a load is a child that outlasts the patience window
                // (`_isWaitingOn`) and arrives Absolute afterwards.
                //
                // Restoring on every pass instead would make layout the
                // permanent owner of a position the Absolute escape hatch
                // exists to hand to someone ELSE: it would fight a state pose
                // mid-transition, and yank a dragged Moveable home the moment
                // any commit re-flowed the scene. Gating on "we flowed it"
                // repairs the race and leaves every third-party write alone.
                //
                // No authored translation means the origin — still restored.
                // (No delete needed: the node isn't auto-flowed this pass, so
                // the end-of-pass swap forgets it.)
                const authored = this._flowedAsAuto.get(transformId) ?? [0, 0, 0];
                const p = sceneObject.transform.position;
                if (p.x !== authored[0] || p.y !== authored[1] || p.z !== authored[2]) {
                    sceneObject.setPosition(new Vector3(authored[0], authored[1], authored[2]), null);
                }
                // The rest position holds the flowed one (see the flow write
                // below); hand that back too.
                setRestPose(sceneObject, { position: authored });
            }
            if ((!isAbsolute || placedByInset) && parentIsContainer) {
                // Position: corner-based Y-down → center-based Y-up
                // All three axes get center-offset (child center relative to parent center).
                // Y is additionally flipped (layout Y-down → Babylon Y-up).
                //
                // ...except depth for an inset child. The inset is 2D by
                // definition, and the solver left `resolvedPosition.z` at the
                // node's own value rather than a flowed one, so converting it
                // as a cross-axis placement would drag the overlay off the
                // depth its transform states. Kept as authored.
                //
                // Read from the same source as the Absolute repair above: if
                // this child was flowed as auto on an earlier pass, then
                // outside Play its authored data holds the depth WE wrote (the
                // rest position, below), and reading that back would walk the
                // overlay away from the depth it states. The recorded
                // pre-flow translation is the intent; the authored data is the
                // fallback for a child layout never flowed.
                const insetZ = placedByInset
                    ? (this._flowedAsAuto.get(transformId)?.[2]
                        ?? sceneObject.getSceneObjectData().transform?.translation?.[2]
                        ?? 0)
                    : null;
                // A fitted node occupies its box times its fit scale.
                const flowSize = flowScale !== undefined
                    ? { x: size.x * flowScale, y: size.y * flowScale, z: size.z * flowScale }
                    : size;
                const babylonPos = new Vector3(
                    pos.x - parentSize.x / 2 + flowSize.x / 2,
                    parentSize.y / 2 - pos.y - flowSize.y / 2,
                    insetZ ?? (pos.z - parentSize.z / 2 + flowSize.z / 2),
                );
                const slotCenter = placementsNow ? { x: babylonPos.x, y: babylonPos.y, z: babylonPos.z } : null;
                // Centre hook: first provider (precedence order) that
                // ANSWERS wins — an abstaining winner falls through. On the
                // shipped smart-component config (MorphStretch + Model
                // co-resident) MorphStretch answers only for a kit asset
                // with an inner reference — pivot at the box centre — and
                // abstains otherwise; then
                // Model's bounds centre, translation-invariant and correct
                // on receiver-realized geometry, applies exactly as on a
                // bare model.
                let center: { x: number; y: number; z?: number } | null = null;
                for (const p of providers) {
                    center = p.getLayoutContentCenter?.() ?? null;
                    if (center) break;
                }
                // A node with no provider that hugs its content is seated by
                // that content's centre, at the scale it will have.
                center ??= this._heldCenterOf(node, sceneObject, flowScale ?? 1);
                if (center) {
                    babylonPos.x -= center.x;
                    babylonPos.y -= center.y;
                    // z is optional on the hook (2D providers omit it) —
                    // presence, not truthiness: a reported 0 is an answer.
                    if (center.z != null) babylonPos.z -= center.z;
                }
                // Seat: a fitted leaf whose provider asks for `bottom` has
                // its bounds centre on the slot centre at this point (the
                // hook above); drop it by the slot's spare height so the
                // bounds' floor lands on the slot's floor. The content size
                // is measured in the parent basis AFTER the fit write above
                // — the same freshness the centre hook itself relies on.
                // Bounds larger than the slot (an axis the fit excluded)
                // are left centred rather than lifted out of the box.
                if (fitScaleWritten && provider?.layoutFitAnchor === 'bottom') {
                    const content = provider.getLayoutContentSize();
                    const spare = content ? size.y - content.y : 0;
                    if (spare > LAYOUT_EPSILON) babylonPos.y -= spare / 2;
                }
                // Remember the write, so that if this child turns out to be
                // Absolute on a later pass (its LayoutItem attached after we
                // flowed it) the branch above can undo exactly this. Recorded
                // in this pass's set — the end-of-pass swap is what forgets
                // departed nodes — and before the write, which outside Play
                // makes the solved position the authored one: carry an
                // earlier pass's record forward, else take the authored
                // translation as it stands now.
                flowedNow.set(transformId, this._flowedAsAuto.get(transformId)
                    ?? sceneObject.getSceneObjectData().transform?.translation
                    ?? [0, 0, 0]);
                if (placementsNow && slotCenter) noteSolved(placementsNow, sceneObject, nodeMap.get(node.parentId), parentSize, slotCenter, flowSize, babylonPos);
                sceneObject.setPosition(babylonPos, null);
                // The solved position is the REST position, as a claimed
                // scale is: a state that keys no position returns to it, and
                // a save writes it, whatever states the child carries.
                setRestPose(sceneObject, { position: [babylonPos.x, babylonPos.y, babylonPos.z] });
                managedNow.set(sceneObject, { position: true, scale: false });
            }

            // Scale rule: leaf-scale ONLY plain managed leaves. Any node
            // whose visuals are self-rendered must stay at identity:
            //   - nodes with children — their "size" is a logical frame;
            //     writing scale would compound onto children. Opted-in free
            //     geometry groups instead preserve their authored scale;
            //     a Layout container still owns a logical box, not geometry;
            //   - providers (Text) — glyphs are sized by fontSize; writing
            //     the slot size onto the node stretches them.
            // Identity is actively RESTORED (not just skipped): a node that
            // was leaf-scaled while it qualified (a childless template slot,
            // Hug of nothing → MIN_VISIBLE_SCALE) must not keep that stale
            // scale once filled — and this also repairs scenes whose exports
            // baked such a scale (Model nodes get the load-boundary
            // normalization in normalizeLegacyBakedSlotScale on top).
            const hasChildren = node.childrenIds.length > 0;
            if (managed) {
                // A receiver realizes the resolved size itself (morphs, not
                // scale), so it joins the identity branch below.
                if (receiver) {
                    receiver.setResolvedSize({ x: size.x, y: size.y, z: size.z });
                    fedNow.add(receiver);
                }
                // Resolved-width feedback for reflowing providers (Text
                // wraps to its slot). Hug with no maxWidth feeds null: the
                // provider's own measurement sized that axis, so wrapping to
                // it would echo the solver's output back as its input.
                // Convergence lives provider-side (an unchanged width is a
                // no-op, and a changed one re-invalidates at most one
                // settling pass), so this feed cannot ping-pong. Every
                // implementing provider is fed — the width is per-node fact,
                // not a precedence race.
                //
                // "Hug" is not the same question as "did this width come from
                // the provider". A Hug cross-axis under `alignCross: Stretch`
                // is resolved to the SLOT (LayoutEngine stretches it without
                // rewriting sizeMode), and the spec says so out loud — a
                // Fixed width, a Fill share, or a Stretch cross-axis slot is
                // the wrap width. Keying on the mode fed null there, so a
                // stretched Text kept its authored breaks and overflowed its
                // column. Key on the fact instead: the measure pass bakes a
                // Hug axis's own measurement back into layoutBox.size, so a
                // solved width that still equals it is the provider's own
                // and must not be echoed; one that differs is a slot.
                const hugMeasured = node.layoutBox.size.x;
                const widthIsOwnMeasurement =
                    node.layoutBox.sizeMode.x === 'Hug'
                    && Math.abs(size.x - hugMeasured) <= LAYOUT_EPSILON;
                // Own-measurement is not the same as unconstrained: this
                // node's parent may have stretched it to a width the parent
                // itself hugged FROM this very node, in which case the real
                // constraint (if there is one) lives further out. See
                // _findAncestorWrapWidth.
                const slotWidth = widthIsOwnMeasurement
                    ? this._findAncestorWrapWidth(tree, node, parent)
                    : size.x;
                // A Hug ceiling remains an input after the paragraph fits.
                // Dropping it would alternate capped wrapping with an
                // unconstrained measurement. A narrower ancestor still wins.
                const ceiling = node.layoutBox.sizeMode.x === 'Hug'
                    ? LayoutItem.hugCeiling(node.layoutBox.max.x) : null;
                const wrapWidth = ceiling != null ? Math.min(ceiling, slotWidth ?? ceiling) : slotWidth;
                // Where anchored text sits is the slot layout imposed (the
                // stretched size, or the nearest ancestor's width), clamped by
                // the ceiling as the wrap width is. With no slot imposed the
                // node keeps its own box, so a short left/right-anchored label
                // under a ceiling does not shift.
                const alignSlot = slotWidth == null ? null : wrapWidth;
                for (const p of providers) {
                    if (!p.setLayoutResolvedWidth) continue;
                    p.setLayoutResolvedWidth(wrapWidth, alignSlot);
                    fedWidthNow.set(p, { owner: sceneObject, width: wrapWidth });
                }
                // The veto: a provider that declares a fit and did NOT get
                // one written keeps its node scale — the measurement already
                // includes it (a model's gizmo scale) — so layout neither
                // writes nor claims the field. That covers `fit: none`, and
                // it equally covers a fit nothing constrained (every axis
                // Hug, or the model still in flight with no bounds to
                // measure): an unanswered fit must leave the author's scale
                // alone, never reset it to identity.
                //
                // EXCEPT when the current scale is a claimed solver write
                // (this node was leaf-scaled before the provider attached —
                // empty Hug slot → MIN_VISIBLE_SCALE, or a slot size): then
                // the stale-scale repair runs ONCE — identity restore
                // without re-claiming — and from the next pass on the scale
                // belongs to the user.
                const vetoScale = (!hasChildren || provider instanceof GeometrySizeProvider)
                    && !receiver && !!fit && !fitScaleWritten;
                const scaleIsSolverWrite = sceneObject.getRuntimeOwnedTransformFields().scale;
                // A slot is a container by declaration. Empty, it is still the
                // frame content will stand in: leaf-scaling it to its (depth-less)
                // box would squash it to a millimetre, and whatever is moved in
                // with its world pose kept would be skewed against that scale.
                const isSlot = sceneObject.getBehaviors().some(isContentFitSource);
                let claimScale = false;
                if (flowScale !== undefined) {
                    // Written above; the fit is layout's.
                    claimScale = true;
                } else if (!hasChildren && !provider && !receiver && !isSlot) {
                    const babylonScale = new Vector3(
                        size.x || MIN_VISIBLE_SCALE,
                        size.y || MIN_VISIBLE_SCALE,
                        size.z || MIN_VISIBLE_SCALE,
                    );
                    sceneObject.setScale(babylonScale, null);
                    claimScale = true;
                } else if (fitScaleWritten) {
                    // The fit above IS this pass's scale write; claiming the
                    // field is what tells captureTransform the number is the
                    // solver's, not an authored gizmo scale to bake.
                    claimScale = true;
                } else if (!vetoScale || scaleIsSolverWrite) {
                    const s = sceneObject.transform.scaling;
                    if (s.x !== 1 || s.y !== 1 || s.z !== 1) {
                        sceneObject.setScale(new Vector3(1, 1, 1), null);
                    }
                    // The one-time repair (vetoScale + solver-written scale)
                    // does NOT re-claim; every other identity restore does.
                    claimScale = !vetoScale;
                    // It still hands the node identity as its rest scale: the
                    // slot size layout wrote there is no longer the node's.
                    if (vetoScale) setRestPose(sceneObject, { scale: [1, 1, 1] });
                }

                // Ownership marks follow the writes: asserting branches (a
                // written slot size or a held identity) claim the scale
                // field; a scale-preserving provider's leaf gets no claim,
                // so captureTransform keeps the user's authored gizmo scale.
                if (claimScale) {
                    const marks = managedNow.get(sceneObject) ?? { position: false, scale: false };
                    marks.scale = true;
                    if (derivedFit) marks.fitted = true;
                    managedNow.set(sceneObject, marks);
                }

                // A claimed scale is layout's number, derived on every solve,
                // so it is also the node's REST scale: what a save writes and
                // what a state reverts to, rather than a Properties baseline
                // primed before the solve changed it. A fit is taken as
                // derived; any other claimed scale was written above, so the
                // live scale is layout's here.
                if (claimScale) {
                    const s = sceneObject.transform.scaling;
                    setRestPose(sceneObject, { scale: derivedFit ?? [s.x, s.y, s.z] });
                }
            }
        });

        // Departure sweep: anything fed last pass but not this one has left
        // layout management — tell it to drop the stale resolved box.
        for (const receiver of this._fedReceivers) {
            if (!fedNow.has(receiver)) receiver.clearResolvedSize?.();
        }
        this._fedReceivers = fedNow;
        // Same sweep for wrap widths, but a departure is a HANDOFF rather
        // than a retraction (#709): the node keeps whatever its own
        // LayoutItem entitles it to. Feeding a blind null here would unwrap a
        // paragraph that still carries a standing Fixed width, and an
        // event-ordered retract-then-refeed would race this microtask-batched
        // sweep. Forced, because this Layout is still findable above the node
        // mid-pass and would otherwise veto its own handoff.
        for (const [provider, { owner }] of this._fedWidthProviders) {
            if (fedWidthNow.has(provider)) continue;
            if (owner.transform.isDisposed()) continue;
            // ...unless the node belongs to a different tree now. A reparent
            // invalidates neither container, so this map can hold a node that
            // moved away arbitrarily long ago, and this sweep runs whenever we
            // next re-solve for an unrelated reason. Handing that node its
            // standalone entitlement overwrites the width its new container
            // legitimately solved for it.
            if (!this._ownsDeparture(owner)) continue;
            LayoutItem.feedStandaloneWidth(owner, true);
        }
        this._fedWidthProviders = fedWidthNow;
        // Same sweep for the auto-flow bookkeeping: only nodes flowed THIS
        // pass carry forward. Ids are session-unique, so a dropped entry can
        // never mis-trigger the Absolute repair on some other node.
        this._flowedAsAuto = flowedNow;
        this._solvedPlacements = placementsNow;

        // Mirror ownership marks onto the children (the core runtime-writer
        // registry, #662/#687), retracting this layout's claim on anything
        // that departed. Claims are owner-keyed, so a child that moved to
        // another container keeps its new owner's marks even if we sweep
        // after that owner solved.
        for (const [child, marks] of managedNow) {
            child.setRuntimeTransformFieldOwner(this, { position: marks.position, scale: marks.scale });
        }
        for (const child of this._managedFields.keys()) {
            if (!managedNow.has(child)) {
                child.setRuntimeTransformFieldOwner(this, { position: false, scale: false });
            }
        }
        this._managedFields = managedNow;

        this._applyStackingOrder(tree, nodeMap);
    }

    /**
     * Deterministic paint order for laid-out content, in document order.
     *
     * Why it is needed rather than the geometry's own depth: layout stacks a
     * container's own background BEHIND its content (a body slab sits a
     * fraction of a millimetre back from the node it belongs to), so an
     * overlay authored AFTER a full-slot image is, in pure Z terms, behind
     * that image while its text sits in front of it. Depth-sorted, the
     * overlay's fill vanishes and only its glyphs survive: a chip with no
     * chip behind its label.
     *
     * So laid-out content paints in DFS pre-order instead — first child at the
     * bottom of the stack, last child on top — which is the CSS and Figma
     * convention every template is authored to.
     *
     * One index per node, and nothing else. A container's background and the
     * content it frames are separate nodes, and a child is always visited
     * after its parent, so the traversal order alone puts content above the
     * container — there is no second number for a node to reserve.
     *
     * `renderingGroupId` is deliberately NOT written. Babylon draws groups in
     * order, before anything inside them, so setting one would be layout
     * declaring which group laid-out content belongs to; ordering content
     * against its siblings works in whatever group each mesh is already in,
     * and a mesh another subsystem put in its own group stays there.
     *
     * Limits, stated because they are not obvious: this reaches a node's
     * direct meshes only, it rewrites `alphaIndex` on every laid-out mesh on
     * every pass, and `alphaIndex` orders transparent draws — opaque geometry
     * still sorts by depth.
     */
    private _applyStackingOrder(tree: LayoutTree, nodeMap: Map<string, SceneObject>): void {
        let paintIndex = 0;
        tree.traverse((node) => {
            const index = paintIndex++;
            const sceneObject = nodeMap.get(node.id);
            if (!sceneObject) return;
            const root = sceneObject.transform as unknown as {
                getBoundingInfo?: unknown;
                getChildMeshes: (directOnly: boolean) => AbstractMesh[];
            };
            const meshes = typeof root.getBoundingInfo === "function"
                ? [sceneObject.transform as unknown as AbstractMesh]
                : root.getChildMeshes(true);
            for (const mesh of meshes) {
                mesh.alphaIndex = index;
            }
        });
    }
}

/** What {@link Layout} writes on a child: the fields it claims, and whether its scale is a fit. */
interface ManagedMarks {
    position: boolean;
    scale: boolean;
    fitted?: boolean;
}

/**
 * Make a solved position or scale the node's rest value: the same field of a
 * held Properties baseline (what capture saves and a state reverts to while
 * one is active), and, outside Play, its authored translation or scale. Play
 * writes nothing that survives Stop. An object with no authored transform yet
 * gets one first, as a save would make it. Authored TRS via the internal
 * reference — getSceneObjectData() clones.
 */
function setRestPose(
    sceneObject: SceneObject,
    pose: { position?: readonly [number, number, number]; scale?: readonly [number, number, number] },
): void {
    const { position: p, scale: s } = pose;
    if (!sceneObject.traceScene.isPlayMode()) {
        if (!sceneObject._getTransformData()) sceneObject.captureTransform();
        const authored = sceneObject._getTransformData();
        if (authored && p) authored.translation = [p[0], p[1], p[2]];
        if (authored && s) authored.scale = [s[0], s[1], s[2]];
    }
    sceneObject.getBehavior(Properties)?.setRestPose({
        position: p && new Vector3(p[0], p[1], p[2]),
        scale: s && new Vector3(s[0], s[1], s[2]),
    });
}

/**
 * Re-solve the nearest self-or-ancestor Layout. The shared invalidation
 * edge for content behaviors whose contribution to a solve changed (Text
 * re-render, Model load/detach, MorphStretch authored-size commit,
 * LayoutItem detach).
 */
export function invalidateNearestLayout(sceneObject: SceneObject): void {
    let obj: SceneObject | null = sceneObject;
    while (obj) {
        const layout = obj.getBehavior(Layout);
        if (layout) { layout.invalidate(); return; }
        obj = obj.getParent();
    }
    // No container anywhere above: the node's own LayoutItem is its box
    // authority (#709). Falling through to the standalone feed is what makes
    // attach order, text edits, font changes and `wrap` toggles self-heal
    // through the edge they already use, with no change in text.ts.
    //
    // Deferred by one microtask, and that is the attach-order case rather
    // than a hedge: `addBehavior` runs `load()` BEFORE pushing the behavior,
    // so a Text publishing its first measurement during load is not yet in
    // `getBehaviors()` and a feed here would reach nobody. By the microtask
    // it is registered. The solver batches on the same edge, so this adds no
    // new timing class.
    queueMicrotask(() => {
        if (sceneObject.transform.isDisposed()) return;
        // Re-check: a container may have claimed the node in between.
        LayoutItem.feedStandaloneWidth(sceneObject);
    });
}

/**
 * Load-boundary migration for pre-provider exports.
 *
 * Before Model was a scale-preserving LayoutSizeProvider, a layout-managed
 * Model leaf was leaf-scaled every pass — `setScale(resolvedSize)` — and
 * capture baked that solver output into exported TRS. Under the provider
 * rules the same TRS scale would read as the USER's gizmo scale: preserved
 * forever and multiplied into the measurement.
 *
 * A baked slot scale is recognizable because it is the pre-provider
 * solver's statically predictable output, in meters: per axis the authored
 * slot size (Fixed and Hug both resolved to the authored number — Model
 * provided no measurement then) or MIN_VISIBLE_SCALE (a zero-size axis).
 * Genuine gizmo scale is a unitless multiplier with no relation to the
 * slot box, so a false positive needs all three axes to coincide within
 * LAYOUT_EPSILON simultaneously. Two cases are deliberately left alone
 * rather than risk clearing a real gizmo scale: Fill axes (the legacy bake
 * was a solver-distributed share no load-time check can predict) and
 * property-bound sizes (the authored number isn't known here).
 *
 * An export bake (`bevel_*.glb`, `morphbake_*.glb`) is never such a leaf,
 * whatever Model its `src` implies on load. The export wrote its geometry
 * under exactly this TRS, so the scale is the size the bake renders at: a
 * Bevel'd card body is a unit cube scaled to its fixed slot, and clearing
 * that draws a 1 m cube. A saved built-in primitive (`./assets/primitive_*.glb`)
 * is the same: a unit mesh under a scale that is its size.
 *
 * Runs per node at the load boundary (SceneCreation.applyComponents),
 * before behaviors construct — pure behavior data + the preloaded
 * transform. Normalizes both the live scaling and the authored TRS.
 */
export function normalizeLegacyBakedSlotScale(sceneObject: SceneObject): void {
    if (isBakedGeometrySrc(sceneObject.src)) return;
    // A saved built-in primitive (see above). A Fixed slot makes its scale equal
    // the authored size on all three axes, the very coincidence checked below.
    if (typeof sceneObject.src === 'string' && getSavedPrimitiveType(sceneObject.src)) return;
    const behaviors = sceneObject._getBehaviorData();
    if (!behaviors?.some(b => b['@type'] === 'Model')) return;
    const found = behaviors.find(b => b['@type'] === 'LayoutItem' || b['@type'] === 'Layout');
    if (!found) return;
    // Every LayoutItemInput field is optional, so the behavior record widens
    // into it directly — an unsized slot is an absent field, not a cast.
    const item: LayoutItemInput & { '@type': string } = found;

    const scaling = sceneObject.transform.scaling;
    if (scaling.x === 1 && scaling.y === 1 && scaling.z === 1) return;

    const axes: Array<{ scale: number; mode: string; authored: unknown }> = [
        { scale: scaling.x, mode: item.widthMode ?? 'Fixed', authored: item.width },
        { scale: scaling.y, mode: item.heightMode ?? 'Fixed', authored: item.height },
        { scale: scaling.z, mode: item.depthMode ?? 'Fixed', authored: item.depth },
    ];
    for (const axis of axes) {
        if (Math.abs(axis.scale - MIN_VISIBLE_SCALE) < LAYOUT_EPSILON) continue;
        if (axis.mode === 'Fill') return;
        if (axis.authored != null && typeof axis.authored !== 'number') return;
        const predicted = typeof axis.authored === 'number' ? axis.authored : DEFAULT_SIZE;
        if (Math.abs(axis.scale - predicted) >= LAYOUT_EPSILON) return;
    }

    // Residue confirmed: normalize live + authored TRS so the model shows
    // at its natural size and the measurement starts from a clean basis.
    // Authored TRS via the internal reference — getSceneObjectData() clones.
    sceneObject.transform.scaling.set(1, 1, 1);
    const t = sceneObject._getTransformData();
    if (t?.scale) t.scale = [1, 1, 1];
}
